import { GAME_CONFIG, type PremiumOfferConfig } from "../config/gameConfig";
import { renderTapBoostStatus } from "./tapBoost";
import { renderSpendFeedback, type SpendFeedback } from "./spendFeedback";
import { FACET_QUARTER_ART, MAKERS_CLUB_ART, OFFLINE_REWARD_ART, PREMIUM_SHOP_ART, TROPHY_HALL_ART } from "../config/artAssets";
import { energyFillPercent, quoteEnergyRefillCost, secondsUntilNextEnergy } from "../domain/energy";
import { amount } from "../domain/gameMath";
import { quoteCommunityFinalBonus } from "../domain/communityBonus";
import {
  dailyRouteResetsAt,
  dailyRouteReward,
  dailyRewardForStreak,
  dailyTaskProgress,
  getDailyTaskDefinition,
  getSeasonTaskDefinition,
  nextStreakMilestone,
  premiumOfferAvailability,
  premiumOfferById,
  seasonTaskProgress,
  seasonTaskUnlocked,
} from "../domain/liveOps";
import { confirmedTapProduction, quoteTapProductionGrant } from "../domain/premium";
import {
  achievementProgress,
  coreUpgradeLevel,
  dailyGoalsCompletedCount,
  dailyGoalsComplete,
  deriveWorkshopStage,
  getNextUpgradeLevel,
  getPurchasedUpgradeCount,
  getUpgradeLevel,
  getUpgradeLevelConfig,
  readyRewardSummary,
} from "../domain/progression";
import type {
  AchievementCategory,
  AchievementId,
  CollectibleId,
  DailyTaskCode,
  DiamondSkinId,
  EnergyUpgradeCode,
  GameState,
  ProgressSegment,
  PremiumOfferId,
  ProductionUpgradeCode,
  SectionId,
  TaskMetric,
  UpgradeCode,
} from "../domain/types";
import type { TranslationKey, Translator } from "../i18n/locales";
import {
  formatAmount,
  formatCurrencyAmount,
  formatCurrencyUnit,
  formatEnergyRecoveryGain,
  formatEnergyRecoveryRate,
  formatSeasonCountdown,
  formatSharePpm,
  formatUsdtCents,
  getSeasonCountdownTarget,
  getSeasonCountdownParts,
  formatOfflineDuration,
  formatPassiveRate,
  formatSeasonTime,
  formatServerDuration,
  formatUsdtMicros,
  formatUtcDate,
} from "./formatters";
import { closeIcon, currencyIcon } from "./icons";
import { pendingSeasonClaim } from "./seasonPresentation";
import { leaderboardGate } from "./communitySheets";
import { escapeHtml } from "./templates";
import type { PremiumShopRelic } from "../phaser/PremiumShopScene";

export type AchievementFilter = "all" | AchievementCategory;
export type FriendFilter = "all" | 1 | 2;

type TemplateContext = {
  state: GameState;
  serverNow: string;
  t: Translator;
};

const upgradeCodes: readonly ProductionUpgradeCode[] = ["mechanicalCutter", "precisionCutter"];

const DAILY_TASK_COPY_CODES = new Set([
  "tap-royal-shift", "energy-royal-cycle", "primary-royal-order", "primary-grand-caravan",
  "passive-royal-stream", "passive-grand-reserve", "arena-royal-series", "arena-double-victory",
  "tap-light-touch",
  "tap-steady-hand",
  "tap-precision",
  "tap-clear-facet",
  "tap-master-tempo",
  "tap-long-cut",
  "energy-deep-cut",
  "energy-full-cycle",
  "primary-first-shift",
  "primary-bright-shift",
  "primary-full-shift",
  "primary-strong-shift",
  "primary-master-shift",
  "primary-grand-order",
  "passive-quiet-current",
  "passive-crystal-pulse",
  "passive-light-stream",
  "passive-engine-room",
  "passive-full-vault",
  "passive-cascade",
  "offline-return",
  "offline-vault",
  "return-second-session",
  "arena-first-duel",
  "arena-double-duel",
  "arena-first-win",
  "arena-match-series",
  "upgrade-workshop",
  "upgrade-workshop-double",
  "venture-tap-echo",
  "venture-primary-spark",
  "invite-master",
  "join-channel",
  "join-chat",
]);
const SEASON_TASK_COPY_CODES = new Set([
  "season-taps-1",
  "season-taps-2",
  "season-taps-3",
  "season-primary-1",
  "season-primary-2",
  "season-primary-3",
  "season-upgrades-1",
  "season-upgrades-2",
  "season-upgrades-3",
  "season-arena-1",
  "season-arena-2",
  "season-arena-3",
  "season-routes-1",
  "season-routes-2",
  "season-routes-3",
  "season-routes-4",
  "season-friends-1",
  "season-friends-2",
  "season-friends-3",
  "season-channel",
  "season-chat",
]);
const PREMIUM_OFFER_COPY_IDS = new Set([
  "offline-reserve-starter-8h",
  "offline-reserve-8h",
  "offline-reserve-12h",
  "offline-reserve-24h",
  "passive-boost-20-24h",
  "passive-boost-20-6h",
  "tap-boost-50-30m",
  "season-start-taps-10",
  "season-start-taps-30",
  "season-start-taps-50",
  "skin-aurora-prism",
  "skin-black-diamond",
  "skin-solar-heart",
  "skin-emerald-abyss",
  "season-01-signet",
]);

function taskCopy(
  scope: "daily" | "seasonTask",
  code: string,
  metric: TaskMetric,
): { title: TranslationKey; body: TranslationKey } {
  const hasAuthoredCopy = scope === "daily" ? DAILY_TASK_COPY_CODES.has(code) : SEASON_TASK_COPY_CODES.has(code);
  return {
    title: hasAuthoredCopy
      ? (`hall.${scope}.${code}.name` as TranslationKey)
      : (`hall.${scope}.generic.name` as TranslationKey),
    body: hasAuthoredCopy
      ? (`hall.${scope}.${code}.body` as TranslationKey)
      : (`hall.task.metric.${metric}` as TranslationKey),
  };
}

const achievementKeys: Record<AchievementId, { name: TranslationKey; body: TranslationKey }> = {
  "first-spark": {
    name: "hall.achievement.first-spark.name",
    body: "hall.achievement.first-spark.body",
  },
  "living-facet": {
    name: "hall.achievement.living-facet.name",
    body: "hall.achievement.living-facet.body",
  },
  "master-rhythm": {
    name: "hall.achievement.master-rhythm.name",
    body: "hall.achievement.master-rhythm.body",
  },
  "light-architect": {
    name: "hall.achievement.light-architect.name",
    body: "hall.achievement.light-architect.body",
  },
  "makers-circle": {
    name: "hall.achievement.makers-circle.name",
    body: "hall.achievement.makers-circle.body",
  },
  "perfect-facet": {
    name: "hall.achievement.perfect-facet.name",
    body: "hall.achievement.perfect-facet.body",
  },
  "crystal-reserve": {
    name: "hall.achievement.crystal-reserve.name",
    body: "hall.achievement.crystal-reserve.body",
  },
  "silent-current": {
    name: "hall.achievement.silent-current.name",
    body: "hall.achievement.silent-current.body",
  },
  "arena-guardian": {
    name: "hall.achievement.arena-guardian.name",
    body: "hall.achievement.arena-guardian.body",
  },
  "season-one-participant": {
    name: "hall.achievement.season-one-participant.name",
    body: "hall.achievement.season-one-participant.body",
  },
  "season-two-participant": { name: "hall.achievement.season-two-participant.name", body: "hall.achievement.season-two-participant.body" },
  "streak-seven": {
    name: "hall.achievement.streak-seven.name",
    body: "hall.achievement.streak-seven.body",
  },
  "streak-fifteen": {
    name: "hall.achievement.streak-fifteen.name",
    body: "hall.achievement.streak-fifteen.body",
  },
  "streak-thirty": {
    name: "hall.achievement.streak-thirty.name",
    body: "hall.achievement.streak-thirty.body",
  },
  "chain-conqueror": {
    name: "hall.achievement.chain-conqueror.name",
    body: "hall.achievement.chain-conqueror.body",
  },
  "branch-master": {
    name: "hall.achievement.branch-master.name",
    body: "hall.achievement.branch-master.body",
  },
  "workshop-sovereign": {
    name: "hall.achievement.workshop-sovereign.name",
    body: "hall.achievement.workshop-sovereign.body",
  },
  "pvp-first-win": {
    name: "hall.achievement.pvp-first-win.name",
    body: "hall.achievement.pvp-first-win.body",
  },
  "pvp-ten-wins": {
    name: "hall.achievement.pvp-ten-wins.name",
    body: "hall.achievement.pvp-ten-wins.body",
  },
  "arena-ten-matches": {
    name: "hall.achievement.arena-ten-matches.name",
    body: "hall.achievement.arena-ten-matches.body",
  },
  "arena-fifty-matches": {
    name: "hall.achievement.arena-fifty-matches.name",
    body: "hall.achievement.arena-fifty-matches.body",
  },
  "active-friend-one": {
    name: "hall.achievement.active-friend-one.name",
    body: "hall.achievement.active-friend-one.body",
  },
  "active-friend-three": {
    name: "hall.achievement.active-friend-three.name",
    body: "hall.achievement.active-friend-three.body",
  },
  "active-friend-ten": {
    name: "hall.achievement.active-friend-ten.name",
    body: "hall.achievement.active-friend-ten.body",
  },
  "top-ten-percent": {
    name: "hall.achievement.top-ten-percent.name",
    body: "hall.achievement.top-ten-percent.body",
  },
  "top-hundred": {
    name: "hall.achievement.top-hundred.name",
    body: "hall.achievement.top-hundred.body",
  },
  "top-ten": {
    name: "hall.achievement.top-ten.name",
    body: "hall.achievement.top-ten.body",
  },
  "season-champion": {
    name: "hall.achievement.season-champion.name",
    body: "hall.achievement.season-champion.body",
  },
};

export function collectionCatalogIds(extraIds: readonly CollectibleId[] = []): readonly CollectibleId[] {
  const ids: CollectibleId[] = [
    ...GAME_CONFIG.progress.achievements.map((achievement) => achievement.id),
    GAME_CONFIG.rewards.participationCollectibleId,
  ];
  for (const offer of GAME_CONFIG.premium.offers) {
    if (offer.collectibleId !== undefined) ids.push(offer.collectibleId);
  }
  return [...new Set([...ids, ...extraIds])];
}

export function collectionCatalogOwnedCount(items: readonly { readonly id: CollectibleId }[]): number {
  return new Set(items.map((item) => item.id)).size;
}

function collectionCatalogTotal(items: GameState["collection"]["items"]): number {
  return collectionCatalogIds(items.map((item) => item.id)).length;
}

function currency(value: string, context: TemplateContext, compact = false, liveKey?: "available"): string {
  const locale = context.state.settings.locale;
  const valueAttribute =
    liveKey === undefined ? "" : ` data-live-value="${liveKey}"${compact ? ' data-live-compact="true"' : ""}`;
  const unitAttribute = liveKey === undefined ? "" : ` data-live-unit="${liveKey}"`;
  return `<span class="v2-currency">${currencyIcon()}<span${valueAttribute}>${escapeHtml(formatAmount(value, locale, compact))}</span><span class="visually-hidden"${unitAttribute}> ${escapeHtml(
    formatCurrencyUnit(value, locale),
  )}</span></span>`;
}

function stageName(stage: GameState["workshopStage"], t: Translator): string {
  return t(`workshop.stage.${stage}` as TranslationKey);
}

function nextWorkshopAppearance(state: GameState): { stage: 2 | 3 | 4; steps: number } | null {
  if (state.workshopStage === 4) return null;
  const stage = (state.workshopStage + 1) as 2 | 3 | 4;
  const threshold = GAME_CONFIG.workshop.visualStageUpgradeThresholds[stage - 1] ?? 0;
  return { stage, steps: Math.max(1, threshold - getPurchasedUpgradeCount(state)) };
}

function stageAfterNextUpgrade(state: GameState, code: ProductionUpgradeCode): GameState["workshopStage"] {
  const next = getNextUpgradeLevel(state, code);
  if (next === null) return state.workshopStage;
  const upgrades = { ...state.upgrades };
  if (code === "precisionCutter") upgrades.precisionCutterLevel = next.level;
  else upgrades.mechanicalCutterLevel = next.level;
  return deriveWorkshopStage({ upgrades });
}

function upgradeName(code: ProductionUpgradeCode, t: Translator): string {
  return t(code === "precisionCutter" ? "workshop.precision.name" : "workshop.mechanical.name");
}

function upgradeBody(code: ProductionUpgradeCode, t: Translator): string {
  return t(code === "precisionCutter" ? "workshop.precision.body" : "workshop.mechanical.body");
}

function upgradeEffect(code: ProductionUpgradeCode, value: string, t: Translator, locale: GameState["settings"]["locale"]): string {
  return code === "precisionCutter"
    ? t("workshop.tapEffect", { value: formatAmount(value, locale) })
    : t("workshop.passiveEffect", { value: formatPassiveRate(value, locale) });
}

function effectDisplay(
  code: ProductionUpgradeCode,
  value: string,
  t: Translator,
  locale: GameState["settings"]["locale"],
): { value: string; unit: string; spoken: string } {
  if (code === "precisionCutter") {
    return {
      value: formatAmount(value, locale),
      unit: t("workshop.unitPerTap"),
      spoken: upgradeEffect(code, value, t, locale),
    };
  }
  return {
    value: formatPassiveRate(value, locale).replace(/\/[сs]$/u, ""),
    unit: t("workshop.unitPerSecond"),
    spoken: upgradeEffect(code, value, t, locale),
  };
}

function effectComparison(
  code: ProductionUpgradeCode,
  currentValue: string,
  nextValue: string,
  t: Translator,
  locale: GameState["settings"]["locale"],
  extraClass = "",
): string {
  const current = effectDisplay(code, currentValue, t, locale);
  const next = effectDisplay(code, nextValue, t, locale);
  const delta = effectDisplay(code, (amount(nextValue) - amount(currentValue)).toString(), t, locale);
  return `<div class="machine-readout${extraClass === "" ? "" : ` ${extraClass}`}" role="img" aria-label="${escapeHtml(
    t("workshop.effectComparisonA11y", { current: current.spoken, next: next.spoken }),
  )}">
    <div class="machine-effect machine-effect--current" aria-hidden="true"><span>${escapeHtml(
      t("workshop.current"),
    )}</span><strong>${escapeHtml(current.value)}</strong><small>${escapeHtml(current.unit)}</small></div>
    <div class="machine-flow" aria-hidden="true"><i></i><strong>+${escapeHtml(delta.value)}</strong><i></i></div>
    <div class="machine-effect machine-effect--next" aria-hidden="true"><span>${escapeHtml(
      t("workshop.next"),
    )}</span><strong>${escapeHtml(next.value)}</strong><small>${escapeHtml(next.unit)}</small></div>
  </div>`;
}

function upgradeActionLabel(code: ProductionUpgradeCode, currentLevel: number, t: Translator): string {
  if (code === "precisionCutter") return t("workshop.upgradePrecisionAction");
  return t(currentLevel === 0 ? "workshop.launchMechanicalAction" : "workshop.upgradeMechanicalAction");
}

function remainingAppearanceSteps(count: number, locale: GameState["settings"]["locale"], t: Translator): string {
  if (locale === "en") return t(count === 1 ? "workshop.appearanceStepsOne" : "workshop.appearanceStepsMany", { count });
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return t("workshop.appearanceStepsMany", { count });
  if (last === 1) return t("workshop.appearanceStepsOne", { count });
  if (last >= 2 && last <= 4) return t("workshop.appearanceStepsFew", { count });
  return t("workshop.appearanceStepsMany", { count });
}

function levelRail(code: UpgradeCode, currentLevel: number, t: Translator): string {
  const levels = GAME_CONFIG.upgrades[code].levels.filter((level) => level.level > 0);
  const maximumLevel = levels.at(-1)?.level ?? 8;
  const progress = Math.min(100, (currentLevel / maximumLevel) * 100);
  return `<div class="machine-levels" role="progressbar" aria-label="${escapeHtml(
    t("workshop.levelRoute"),
  )}" aria-valuemin="0" aria-valuemax="${maximumLevel}" aria-valuenow="${currentLevel}">
    <span>${escapeHtml(t(code === "energyCapacity" || code === "energyRecovery" ? "energy.level" : "workshop.branchLevel", {
      level: currentLevel,
      max: maximumLevel,
    }))}</span>
    <i><b style="--machine-level-progress:${progress}%"></b></i>
  </div>${GAME_CONFIG.upgrades[code].coreMaxLevel === undefined ? "" : `<p class="machine-mastery-note" data-mastery="${currentLevel >= coreUpgradeLevel(code)}">${escapeHtml(
    t(currentLevel >= coreUpgradeLevel(code) ? "workshop.masterLevels" : "workshop.coreLevels", { level: coreUpgradeLevel(code) }),
  )}</p>`}`;
}

function upgradeTrack(code: ProductionUpgradeCode, context: TemplateContext): string {
  const { state, t } = context;
  const locale = state.settings.locale;
  const currentLevel = getUpgradeLevel(state, code);
  const current = getUpgradeLevelConfig(code, currentLevel);
  const next = getNextUpgradeLevel(state, code);
  const nextStage = stageAfterNextUpgrade(state, code);
  const canAfford = next?.costToReach != null && amount(state.account.available) >= amount(next.costToReach);
  const canBuy = canAfford && state.season.status === "active";
  const missing = next?.costToReach == null ? 0n : amount(next.costToReach) - amount(state.account.available);
  const testId = code === "mechanicalCutter" && currentLevel === 0 ? ' data-testid="buy-cutter"' : "";
  const visualClass = code === "precisionCutter" ? "precision" : "mechanical";
  const actionLabel = upgradeActionLabel(code, currentLevel, t);
  const maximumLevel = GAME_CONFIG.upgrades[code].levels.at(-1)?.level ?? currentLevel;
  const decision =
    next?.costToReach == null
      ? `<div class="machine-complete"><span aria-hidden="true">✦</span><strong>${escapeHtml(t("workshop.max"))}</strong></div>`
      : `${effectComparison(code, current.effectValue, next.effectValue, t, locale)}
        ${
          nextStage > state.workshopStage
            ? `<div class="machine-stage-note"><span aria-hidden="true">✦</span>${escapeHtml(t("workshop.appearanceWillChange"))}</div>`
            : ""
        }
        <div class="machine-actions">
          <button class="machine-upgrade" type="button" data-action="buy-upgrade" data-upgrade="${code}"${testId} ${
            canBuy ? "" : "disabled"
          } aria-label="${escapeHtml(`${actionLabel}, ${formatCurrencyAmount(next.costToReach, locale)}`)}"><span>${escapeHtml(
            actionLabel,
          )}</span>${currency(next.costToReach, context, true)}</button>
        </div>
        ${
          missing > 0n
            ? `<p class="machine-shortfall" data-live-shortfall="${code}" aria-live="polite"><span aria-hidden="true">◇</span><strong data-live-shortfall-value>${escapeHtml(
                t("workshop.shortfall", { value: formatCurrencyAmount(missing.toString(), locale) }),
              )}</strong></p>`
            : ""
        }`;

  return `<section class="machine-module machine-module--${visualClass}" aria-labelledby="${code}-title" data-level="${currentLevel}">
    <div class="machine-figure machine-figure--${visualClass}" aria-hidden="true"><span class="machine-figure__halo"></span></div>
    <header class="machine-heading">
      <span>${escapeHtml(t("workshop.branchLevel", { level: currentLevel, max: maximumLevel }))}</span>
      <h2 id="${code}-title">${escapeHtml(upgradeName(code, t))}</h2>
      <p>${escapeHtml(upgradeBody(code, t))}</p>
      <button class="machine-details" type="button" data-action="open-upgrade-details" data-upgrade="${code}" aria-label="${escapeHtml(
        t("workshop.detailsFor", { name: upgradeName(code, t) }),
      )}"><span aria-hidden="true">i</span></button>
    </header>
    ${levelRail(code, currentLevel, t)}
    <div class="machine-decision">${decision}</div>
  </section>`;
}

function energyStatus(state: GameState, t: Translator): string {
  if (state.energy.current >= state.energy.capacity) return t("energy.full");
  if (state.energy.current === 0) return t("energy.empty");
  return t("energy.next", {
    seconds: secondsUntilNextEnergy(
      state.energy.current,
      state.energy.capacity,
      state.energy.recoverySecondsPerUnit,
      state.meta.energyRemainderMilliseconds,
      state.energy.recoveryMultiplierBasisPoints ?? 10000,
    ),
  });
}

function energyEffectDisplay(
  code: EnergyUpgradeCode,
  value: string,
  t: Translator,
  locale: GameState["settings"]["locale"],
): { value: string; unit: string; spoken: string } {
  if (code === "energyCapacity") {
    return {
      value: formatAmount(value, locale),
      unit: t("energy.capacityUnit"),
      spoken: t("energy.capacityValue", { value: formatAmount(value, locale) }),
    };
  }
  const formattedRate = formatEnergyRecoveryRate(value, locale);
  return {
    value: formattedRate,
    unit: t("energy.recoveryUnit"),
    spoken: t("energy.recoveryRateValue", { value: formattedRate }),
  };
}

function energyEffectComparison(
  code: EnergyUpgradeCode,
  currentValue: string,
  nextValue: string,
  context: TemplateContext,
): string {
  const { state, t } = context;
  const current = energyEffectDisplay(code, currentValue, t, state.settings.locale);
  const next = energyEffectDisplay(code, nextValue, t, state.settings.locale);
  const delta = code === "energyCapacity"
    ? formatAmount(
        (amount(currentValue) > amount(nextValue)
          ? amount(currentValue) - amount(nextValue)
          : amount(nextValue) - amount(currentValue)
        ).toString(),
        state.settings.locale,
      )
    : formatEnergyRecoveryGain(currentValue, nextValue, state.settings.locale);
  return `<div class="machine-readout" role="img" aria-label="${escapeHtml(
    t("energy.changeA11y", { current: current.spoken, next: next.spoken }),
  )}">
    <div class="machine-effect machine-effect--current" aria-hidden="true"><span>${escapeHtml(
      t("workshop.current"),
    )}</span><strong>${escapeHtml(current.value)}</strong><small>${escapeHtml(current.unit)}</small></div>
    <div class="machine-flow" aria-hidden="true"><i></i><strong>+${escapeHtml(delta)}</strong><i></i></div>
    <div class="machine-effect machine-effect--next" aria-hidden="true"><span>${escapeHtml(
      t("workshop.next"),
    )}</span><strong>${escapeHtml(next.value)}</strong><small>${escapeHtml(next.unit)}</small></div>
  </div>`;
}

function energyUpgradeModule(code: EnergyUpgradeCode, context: TemplateContext): string {
  const { state, t } = context;
  const currentLevel = getUpgradeLevel(state, code);
  const current = getUpgradeLevelConfig(code, currentLevel);
  const next = getNextUpgradeLevel(state, code);
  const canAfford = next?.costToReach != null && amount(state.account.available) >= amount(next.costToReach);
  const canBuy = canAfford && state.season.status === "active";
  const isCapacity = code === "energyCapacity";
  const missing = next?.costToReach == null ? 0n : amount(next.costToReach) - amount(state.account.available);
  const name = t(isCapacity ? "energy.capacityName" : "energy.recoveryName");
  const body = t(isCapacity ? "energy.capacityBody" : "energy.recoveryBody");
  const maximumLevel = GAME_CONFIG.upgrades[code].levels.at(-1)?.level ?? currentLevel;
  const decision =
    next?.costToReach == null
      ? `<div class="machine-complete"><span aria-hidden="true">✦</span><strong>${escapeHtml(t("energy.max"))}</strong></div>`
      : `${energyEffectComparison(code, current.effectValue, next.effectValue, context)}
        <div class="machine-actions">
          <button class="machine-upgrade" type="button" data-action="buy-upgrade" data-upgrade="${code}" ${
            canBuy ? "" : "disabled"
          } aria-label="${escapeHtml(`${t(isCapacity ? "energy.upgradeCapacityAction" : "energy.upgradeRecoveryAction")}, ${formatCurrencyAmount(next.costToReach, state.settings.locale)}`)}"><span>${escapeHtml(
            t(isCapacity ? "energy.upgradeCapacityAction" : "energy.upgradeRecoveryAction"),
          )}</span>${currency(next.costToReach, context, true)}</button>
        </div>
        ${
          missing > 0n
            ? `<p class="machine-shortfall" data-live-shortfall="${code}" aria-live="polite"><span aria-hidden="true">◇</span><strong data-live-shortfall-value>${escapeHtml(
                t("workshop.shortfall", { value: formatCurrencyAmount(missing.toString(), state.settings.locale) }),
              )}</strong></p>`
            : ""
        }`;

  return `<section class="machine-module machine-module--energy-${isCapacity ? "capacity" : "recovery"}" aria-labelledby="${code}-title" data-level="${currentLevel}" data-testid="${isCapacity ? "energy-capacity-module" : "energy-recovery-module"}">
    <div class="machine-figure machine-figure--energy-${isCapacity ? "capacity" : "recovery"}" aria-hidden="true"><span class="machine-figure__halo"></span><span id="energy-${isCapacity ? "capacity" : "recovery"}-root" class="energy-mechanism-runtime"></span></div>
    <header class="machine-heading">
      <span>${escapeHtml(t("energy.level", { level: currentLevel, max: maximumLevel }))}</span>
      <h2 id="${code}-title">${escapeHtml(name)}</h2>
      <p>${escapeHtml(body)}</p>
      <button class="machine-details" type="button" data-action="open-section-help" aria-label="${escapeHtml(
        t("workshop.detailsFor", { name }),
      )}"><span aria-hidden="true">i</span></button>
    </header>
    ${levelRail(code, currentLevel, t)}
    <div class="machine-decision">${decision}</div>
  </section>`;
}

export function renderDevelopmentV2(state: GameState, t: Translator): string {
  const context: TemplateContext = { state, serverNow: state.meta.lastProductionAt, t };
  const nextAppearance = nextWorkshopAppearance(state);
  const purchased = getPurchasedUpgradeCount(state);
  const totalPurchases = (Object.keys(GAME_CONFIG.upgrades) as UpgradeCode[]).reduce(
    (total, code) => total + Math.max(0, GAME_CONFIG.upgrades[code].levels.length - 1),
    0,
  );
  return `<div class="realm-page realm-page--workshop" data-testid="development-screen">
    <section class="section-world section-world--workshop" data-stage="${state.workshopStage}" aria-label="${escapeHtml(
      t("workshop.appearanceA11y", { stage: state.workshopStage, name: stageName(state.workshopStage, t) }),
    )}">
      <div class="section-world__shade" aria-hidden="true"></div>
      <div class="section-world__identity">
        <span>${escapeHtml(t("workshop.appearance"))}</span>
        <h2>${escapeHtml(stageName(state.workshopStage, t))}</h2>
        <div class="workshop-hero-progress"><span>${escapeHtml(
          t("workshop.mastery", { level: purchased, total: totalPurchases }),
        )}</span><strong>${escapeHtml(
          nextAppearance === null
            ? t("workshop.appearanceComplete")
            : remainingAppearanceSteps(nextAppearance.steps, state.settings.locale, t),
        )}</strong></div>
        <i class="workshop-hero-meter" role="progressbar" aria-label="${escapeHtml(
          t("workshop.masteryA11y"),
        )}" aria-valuemin="0" aria-valuemax="${totalPurchases}" aria-valuenow="${purchased}"><b style="--appearance-progress:${(purchased / totalPurchases) * 100}%"></b></i>
      </div>
      <button class="section-world__wallet" type="button" data-action="open-balance" aria-label="${escapeHtml(
        t("header.balanceDetails"),
      )}"><span>${escapeHtml(t("workshop.balance"))}</span>${currency(
        state.account.available,
        context,
        true,
        "available",
      )}</button>
      <button class="premium-gate premium-gate--workshop" type="button" data-action="open-premium-shop" aria-label="${escapeHtml(
        t("shop.open"),
      )}"><img src="${escapeHtml(PREMIUM_SHOP_ART.seasonSignet.url)}" alt="" width="${PREMIUM_SHOP_ART.seasonSignet.width}" height="${PREMIUM_SHOP_ART.seasonSignet.height}"/><span><small>${escapeHtml(
        t("shop.kicker"),
      )}</small><strong>${escapeHtml(t("shop.openShort"))}</strong></span></button>
    </section>
    <section class="mechanism-console" aria-labelledby="mechanism-console-title">
      <header class="mechanism-console__header">
        <div><span>${escapeHtml(t("workshop.upgradesTitle"))}</span><h2 id="mechanism-console-title">${escapeHtml(
          t("workshop.consoleTitle"),
        )}</h2><p>${escapeHtml(t("workshop.consoleLead"))}</p></div>
      </header>
      <div class="machine-modules">${upgradeCodes.map((code) => upgradeTrack(code, context)).join("")}${energyUpgradeModule(
        "energyCapacity",
        context,
      )}${energyUpgradeModule("energyRecovery", context)}</div>
    </section>
  </div>`;
}

type TrophyArt = { url: string; width: number; height: number };

function trophyArt(asset: TrophyArt, className: string): string {
  return `<span class="${className}" aria-hidden="true"><img src="${escapeHtml(asset.url)}" alt="" width="${asset.width}" height="${asset.height}" loading="lazy" decoding="async" /></span>`;
}

export function formatDailyResetLabel(state: GameState, serverNow: string, t: Translator): string {
  const now = new Date(serverNow);
  if (Number.isNaN(now.getTime())) return t("hall.resetSoon");
  const reset = new Date(dailyRouteResetsAt(state, serverNow));
  if (Number.isNaN(reset.getTime())) return t("hall.resetSoon");
  const remainingMinutes = Math.max(0, Math.ceil((reset.getTime() - now.getTime()) / 60_000));
  return t("hall.resetClock", {
    hours: Math.floor(remainingMinutes / 60),
    minutes: String(remainingMinutes % 60).padStart(2, "0"),
  });
}

function dailyTask(
  index: number,
  code: DailyTaskCode,
  context: TemplateContext,
): string {
  const { state, t } = context;
  const definition = getDailyTaskDefinition(code);
  const task = dailyTaskProgress(state, code);
  const current = task.current;
  const target = task.target;
  const bounded = current > target ? target : current;
  const complete = task.complete;
  const progress = target === 0n ? 100 : Number((bounded * 100n) / target);
  const copy = taskCopy("daily", code, definition.metric);
  const art =
    definition.metric === "channel-membership" || definition.metric === "chat-membership"
      ? TROPHY_HALL_ART.seasonCrown
      : definition.metric === "passive-produced" || definition.metric === "offline-collections"
      ? TROPHY_HALL_ART.trials.passive
      : definition.metric === "upgrade-purchases"
        ? TROPHY_HALL_ART.trials.upgrade
        : TROPHY_HALL_ART.trials.precision;
  const value = complete
    ? t("progress.completed")
    : t("hall.progressValue", {
        current: formatAmount(bounded.toString(), state.settings.locale),
        target: formatAmount(target.toString(), state.settings.locale),
      });
  const communityTarget = definition.metric === "channel-membership"
    ? "channel"
    : definition.metric === "chat-membership"
      ? "chat"
      : null;
  const membership = communityTarget === null ? null : state.telegram[communityTarget];
  const communityActions = communityTarget === null || complete
    ? ""
    : `<div class="trophy-trial__community-actions">${membership?.joinUrl == null ? "" : `<a href="${escapeHtml(
        membership.joinUrl,
      )}" target="_blank" rel="noopener noreferrer">${escapeHtml(t(communityTarget === "channel" ? "hall.openChannel" : "hall.openChat"))}</a>`}<button type="button" data-action="check-community" data-community="${communityTarget}">${escapeHtml(
        t("hall.checkMembership"),
      )}</button></div>`;
  return `<li class="trophy-trial${complete ? " is-complete" : ""}" data-trial="${index}">
    ${trophyArt(art, "trophy-trial__art")}
    <div class="trophy-trial__body" data-daily-task-code="${escapeHtml(code)}">
      <header><span>${escapeHtml(t("hall.trialNumber", { number: index }))}${state.progress.daily.forcedTaskCode === code ? `<b>${escapeHtml(t("hall.dailyForced"))}</b>` : communityTarget !== null ? `<b>${escapeHtml(t("hall.dailyPriority"))}</b>` : ""}</span><em data-daily-task-value>${escapeHtml(value)}</em></header>
      <h2>${escapeHtml(t(copy.title))}</h2>
      <p>${escapeHtml(t(copy.body, { target: formatAmount(target.toString(), state.settings.locale), minutes: Math.ceil(GAME_CONFIG.retention.qualifyingReturnSeconds / 60) }))}</p>
      <div class="trophy-trial__meter" data-daily-task-meter role="progressbar" aria-label="${escapeHtml(t(copy.title))}" aria-valuemin="0" aria-valuemax="${target}" aria-valuenow="${bounded}"><i style="--trophy-progress:${progress / 100}"></i></div>
      ${communityActions}
    </div>
  </li>`;
}

export function dailyRemainingRewardHint(state: GameState, t: Translator): string {
  const names = state.progress.daily.assignmentCodes
    .filter((code) => !dailyTaskProgress(state, code).complete)
    .map((code) => t(taskCopy("daily", code, getDailyTaskDefinition(code).metric).title));
  return t("hall.rewardNextTasks", { tasks: names.join(" · ") });
}

function todayV2(context: TemplateContext): string {
  const { state, serverNow, t } = context;
  const completeCount = dailyGoalsCompletedCount(state);
  const ready = state.season.status === "active" && dailyGoalsComplete(state);
  const routeReward = dailyRouteReward(state).toString();
  const milestone = nextStreakMilestone(state);
  return `<section class="trophy-daily" data-daily-complete-count="${completeCount}" aria-labelledby="daily-trials-title">
    <header class="trophy-panel-heading">
      <div><span>${escapeHtml(t("hall.dailyKicker"))}</span><h2 id="daily-trials-title">${escapeHtml(
        t("hall.dailyTitle"),
      )}</h2><p>${escapeHtml(t("hall.dailyLead"))}</p></div>
      <div class="trophy-reset"><span>${escapeHtml(t("hall.untilReset"))}</span><strong data-daily-reset-countdown>${escapeHtml(
        formatDailyResetLabel(state, serverNow, t),
      )}</strong></div>
    </header>
    <ol class="trophy-trials">
      ${state.progress.daily.assignmentCodes.map((code, index) => dailyTask(index + 1, code, context)).join("")}
    </ol>
    <div class="trophy-daily-reward${ready ? " is-ready" : ""}${state.progress.daily.rewardClaimed ? " is-claimed" : ""}">
      <div class="trophy-daily-reward__copy"><span>${escapeHtml(t("hall.dailyReward"))}</span><strong>${currency(
        state.progress.daily.rewardClaimed ? state.progress.daily.claimedReward : routeReward,
        context,
      )}</strong><p>${escapeHtml(
        state.progress.daily.rewardClaimed
          ? t("hall.rewardStored")
          : ready
            ? t("hall.rewardReady")
            : dailyRemainingRewardHint(state, t),
      )}</p><button class="trophy-daily-reward__next" type="button" data-action="show-unfinished-daily"${ready || state.progress.daily.rewardClaimed ? " hidden" : ""}>${escapeHtml(t("hall.showRemainingTask"))}</button></div>
      <button type="button" data-action="claim-daily-reward" ${ready && !state.progress.daily.rewardClaimed ? "" : "disabled"}>${escapeHtml(
        state.progress.daily.rewardClaimed
          ? t("hall.dailyClaimed")
          : t("hall.claimDaily", { value: formatCurrencyAmount(routeReward, state.settings.locale) }),
      )}</button>
    </div>
    <button type="button" class="trophy-streak" data-action="open-streak-calendar" data-streak="${state.progress.dailyStreak.current}">
      ${trophyArt(TROPHY_HALL_ART.dailySeal, "trophy-streak__art")}
      <span class="trophy-streak__copy"><small>${escapeHtml(t("hall.streakTitle"))}</small><strong>${escapeHtml(
        t("hall.streakDays", { count: state.progress.dailyStreak.current }),
      )}</strong><span class="trophy-streak__steps" aria-hidden="true">${Array.from({ length: 7 }, (_, index) => `<i class="${index < Math.min(7, state.progress.dailyStreak.current) ? "is-lit" : ""}"></i>`).join("")}</span><p>${escapeHtml(
        milestone === null
          ? t("hall.streakComplete")
          : t("hall.streakNext", {
              day: milestone.day,
              value: formatCurrencyAmount(milestone.bonus, state.settings.locale),
            }),
      )}</p></span><em aria-hidden="true">→</em>
    </button>
  </section>`;
}

export function renderDailyStreakV2(state: GameState, t: Translator): string {
  const current = state.progress.dailyStreak.current;
  const nextDay = Math.min(GAME_CONFIG.liveOps.daily.rewardGrowthCapDay, current + 1);
  const milestones = new Set(GAME_CONFIG.liveOps.daily.streakMilestones.map((milestone) => milestone.day));
  const days = Array.from({ length: GAME_CONFIG.liveOps.daily.rewardGrowthCapDay }, (_, index) => {
    const day = index + 1;
    const classes = [
      day <= current ? "is-complete" : "",
      day === current + 1 ? "is-next" : "",
      milestones.has(day) ? "is-milestone" : "",
    ].filter(Boolean).join(" ");
    return `<li class="${classes}" data-streak-day="${day}"><span>${day}</span><strong>${escapeHtml(
      formatAmount(dailyRewardForStreak(day).toString(), state.settings.locale, true),
    )}</strong>${milestones.has(day) ? `<i aria-label="${escapeHtml(t("hall.streakMilestoneA11y"))}">◆</i>` : ""}</li>`;
  }).join("");
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}"></button>
    <section class="streak-sheet" role="dialog" aria-modal="true" aria-labelledby="streak-sheet-title">
      <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}">${closeIcon()}</button>
      <div class="streak-sheet__hero">${trophyArt(TROPHY_HALL_ART.dailySeal, "streak-sheet__art")}<div><span>${escapeHtml(t("hall.streakKicker"))}</span><h2 id="streak-sheet-title">${escapeHtml(t("hall.streakDays", { count: current }))}</h2><p>${escapeHtml(t("hall.streakSheetLead"))}</p></div></div>
      <div class="streak-sheet__next"><span>${escapeHtml(t("hall.streakNextReward", { day: nextDay }))}</span><strong>${currency(dailyRewardForStreak(nextDay).toString(), { state, serverNow: state.meta.lastProductionAt, t })}</strong></div>
      <ol class="streak-calendar" aria-label="${escapeHtml(t("hall.streakCalendarA11y"))}">${days}</ol>
      <p class="streak-sheet__rule"><i aria-hidden="true">!</i><span><strong>${escapeHtml(t("hall.streakRuleTitle"))}</strong>${escapeHtml(t("hall.streakRuleBody"))}</span></p>
      <button class="streak-sheet__action" type="button" data-action="close-sheet">${escapeHtml(t("hall.streakContinue"))}</button>
    </section>`;
}

function achievementCategoryKey(category: AchievementCategory): TranslationKey {
  if (category === "craft") return "hall.categoryCraft";
  if (category === "season") return "hall.categorySeason";
  return "hall.categoryFriends";
}

function renderCatalogCollectible(
  id: CollectibleId,
  state: GameState,
  t: Translator,
): string {
  const item = state.collection.items.find((candidate) => candidate.id === id);
  const name = collectibleName(id, t);
  const art = trophyArt(collectibleArt(id), "trophy-relic__art");
  if (item !== undefined) {
    return `<li class="trophy-relic trophy-relic--collectible is-claimed" data-testid="collectible-${id}"><button class="trophy-relic__catalog-entry" type="button" data-action="open-collectible" data-collectible="${id}">
      ${art}
      <span class="trophy-relic__body"><header><span>${escapeHtml(
        t(`collection.kind.${item.kind}` as TranslationKey),
      )}</span><em>${escapeHtml(t(`collection.rarity.${item.rarity}` as TranslationKey))}</em></header><strong>${escapeHtml(
        name,
      )}</strong><small>${escapeHtml(t("collection.received"))}: ${escapeHtml(
        formatUtcDate(item.acquiredAt, state.settings.locale),
      )}</small><span class="trophy-relic__collection-mark"><i aria-hidden="true">✓</i>${escapeHtml(t("hall.claimed"))}</span></span></button></li>`;
  }

  const isSkin = isDiamondSkinId(id);
  const body = isSkin
    ? t(`shop.offer.${id}.body` as TranslationKey)
    : t("hall.collectibleSeasonZeroBody");
  const status = isSkin
    ? t("hall.collectibleShopStatus")
    : t("hall.collectibleSeasonZeroStatus");
  const content = `${art}<span class="trophy-relic__body"><header><span>${escapeHtml(
    t(isSkin ? "collection.kind.cosmetic" : "collection.kind.season-signet"),
  )}</span><em>${escapeHtml(status)}</em></header><strong>${escapeHtml(name)}</strong><small>${escapeHtml(
    body,
  )}</small>${isSkin ? `<span class="trophy-relic__collection-mark trophy-relic__collection-mark--shop">${escapeHtml(
    t("hall.collectibleOpenShop"),
  )}<i aria-hidden="true">›</i></span>` : `<span class="trophy-relic__collection-mark trophy-relic__collection-mark--locked"><i aria-hidden="true">◇</i>${escapeHtml(
    t("hall.collectibleLocked"),
  )}</span>`}</span>`;
  return isSkin
    ? `<li class="trophy-relic trophy-relic--collectible is-locked" data-testid="collectible-${id}"><button class="trophy-relic__catalog-entry" type="button" data-action="open-premium-shop" data-shop-relic="skin" aria-label="${escapeHtml(
        t("hall.collectibleOpenShopA11y", { name }),
      )}">${content}</button></li>`
    : `<li class="trophy-relic trophy-relic--collectible is-locked" data-testid="collectible-${id}"><div class="trophy-relic__catalog-entry">${content}</div></li>`;
}

function achievementsV2(context: TemplateContext, filter: AchievementFilter): string {
  const { state, t } = context;
  const achievements = GAME_CONFIG.progress.achievements
    .filter((achievement) => filter === "all" || achievement.category === filter)
    .map((achievement, order) => ({
      achievement,
      order,
      progress: achievementProgress(state, achievement),
      isClaimed: state.progress.claimedAchievementIds.includes(achievement.id),
    }))
    .sort((left, right) => {
      const leftRank = left.isClaimed ? 2 : left.progress.ready ? 0 : 1;
      const rightRank = right.isClaimed ? 2 : right.progress.ready ? 0 : 1;
      return leftRank - rightRank || left.order - right.order;
    });
  const claimed = collectionCatalogOwnedCount(state.collection.items);
  const configuredPermanentIds: CollectibleId[] = [
    GAME_CONFIG.rewards.participationCollectibleId,
    ...GAME_CONFIG.premium.offers.flatMap((offer) => offer.collectibleId === undefined ? [] : [offer.collectibleId]),
  ];
  const permanentIds = [...new Set([
    ...configuredPermanentIds,
    ...state.collection.items.filter((item) => item.kind !== "achievement").map((item) => item.id),
  ])].filter((id) => {
    const item = state.collection.items.find((candidate) => candidate.id === id);
    const kind = item?.kind ?? (isDiamondSkinId(id) ? "cosmetic" : "season-signet");
    if (filter === "all") return true;
    if (filter === "season") return kind === "season-signet";
    if (filter === "craft") return kind === "cosmetic";
    return false;
  });
  const filters: readonly [AchievementFilter, TranslationKey][] = [
    ["all", "hall.filterAll"],
    ["craft", "hall.filterCraft"],
    ["season", "hall.filterSeason"],
    ["friends", "hall.filterFriends"],
  ];
  return `<section class="trophy-vault" aria-labelledby="trophy-vault-title">
    <header class="trophy-panel-heading trophy-panel-heading--vault">
      <div><span>${escapeHtml(t("hall.vaultKicker"))}</span><h2 id="trophy-vault-title">${escapeHtml(
        t("hall.vaultTitle"),
      )}</h2><p>${escapeHtml(t("hall.collectionPermanent"))}</p></div>
      <strong>${escapeHtml(t("hall.collectionCount", { current: claimed, total: collectionCatalogTotal(state.collection.items) }))}</strong>
    </header>
    <div class="trophy-vault-filters" role="group" aria-label="${escapeHtml(t("progress.achievements"))}">${filters
      .map(
        ([value, label]) => `<button type="button" data-action="achievement-filter" data-filter="${value}" aria-pressed="${filter === value}">${escapeHtml(
          t(label),
        )}</button>`,
      )
      .join("")}</div>
    <ul class="trophy-relics">${achievements
      .map(({ achievement, progress, isClaimed }) => {
        const keys = achievementKeys[achievement.id];
        const bounded = progress.comparison === "at-most"
          ? progress.current
          : progress.current > progress.target ? progress.target : progress.current;
        const percent = progress.target === 0n
          ? 100
          : progress.comparison === "at-most"
            ? progress.ready ? 100 : Math.min(99, Number((progress.target * 100n) / (progress.current || 1n)))
            : Number((bounded * 100n) / progress.target);
        const stateLabel = isClaimed
          ? t("hall.claimed")
          : progress.lockedUntilSeasonFinal
            ? t("hall.finalRankPending")
          : progress.ready
            ? t("hall.ready")
            : achievement.metric === "season-rank"
              ? t("hall.rankProgress", { current: formatAmount(progress.current.toString(), state.settings.locale), target: progress.target.toString() })
              : achievement.metric === "season-percentile"
                ? t("hall.percentileProgress", { current: progress.current.toString(), target: progress.target.toString() })
                : t("hall.progressValue", {
                current: formatAmount(bounded.toString(), state.settings.locale),
                target: formatAmount(progress.target.toString(), state.settings.locale),
              });
        return `<li class="trophy-relic${isClaimed ? " is-claimed" : progress.ready ? " is-ready has-action" : " is-locked"}" data-testid="achievement-${achievement.id}">
          ${trophyArt(TROPHY_HALL_ART.achievements[achievement.id], "trophy-relic__art")}
          <div class="trophy-relic__body"><header><span>${escapeHtml(t(achievementCategoryKey(achievement.category)))}</span><em${isClaimed ? ' class="trophy-relic__claimed-label"' : ""}>${escapeHtml(
            stateLabel,
          )}${isClaimed ? '<i class="trophy-relic__check" aria-hidden="true">✓</i>' : ""}</em></header><h2>${escapeHtml(t(keys.name))}</h2><p>${escapeHtml(t(keys.body, { threshold: formatCurrencyAmount(GAME_CONFIG.referrals.activationPrimaryThreshold, state.settings.locale) }))}</p>
            ${isClaimed || progress.lockedUntilSeasonFinal ? "" : `<div class="trophy-relic__meter" role="progressbar" aria-label="${escapeHtml(t(keys.name))}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${percent}"><i style="--trophy-progress:${percent / 100}"></i></div>`}
          </div>
          ${
            progress.ready && !isClaimed
              ? `<button type="button" data-action="claim-achievement" data-achievement="${achievement.id}">${escapeHtml(t("hall.claim"))}</button>`
              : ""
          }
        </li>`;
      })
      .join("")}${permanentIds
      .map((id) => renderCatalogCollectible(id, state, t))
      .join("")}</ul>
  </section>`;
}

function seasonStatus(context: TemplateContext): string {
  const { state, serverNow, t } = context;
  const locale = state.settings.locale;
  const configuredClaimWindow = formatOfflineDuration(GAME_CONFIG.rewards.claimWindowSeconds, locale);
  const pendingClaim = pendingSeasonClaim(state);
  if (pendingClaim) {
    const claimWindow = formatServerDuration(pendingClaim.claimClosesAt, serverNow, locale);
    return `<span>${escapeHtml(t("progress.rewardReadyTitle"))}</span><strong>${escapeHtml(
      t("progress.rewardReadyBody", {
        value: formatUsdtMicros(pendingClaim.referenceUsdtMicros, locale),
      }),
    )}</strong><p>${escapeHtml(t("hall.seasonClaimHint", { stars: pendingClaim.claimPriceStars, window: claimWindow }))}</p><button type="button" data-action="claim-season-reward" data-allocation="${escapeHtml(
      pendingClaim.allocationId,
    )}">${escapeHtml(t("progress.rewardClaimStars", { stars: pendingClaim.claimPriceStars }))}</button>`;
  }
  if (state.season.status === "scheduled") {
    return `<span>${escapeHtml(t("progress.seasonScheduledTitle"))}</span><strong>${escapeHtml(
      t("progress.seasonScheduledBody", { date: formatUtcDate(state.season.startsAt, locale) }),
    )}</strong><p>${escapeHtml(t("hall.seasonScheduledHint"))}</p>`;
  }
  if (state.season.status === "frozen") {
    return `<span>${escapeHtml(t("progress.seasonFrozenTitle"))}</span><strong>${escapeHtml(t("progress.seasonFrozenBody"))}</strong><p>${escapeHtml(
      t("hall.seasonFrozenHint"),
    )}</p>`;
  }
  if (state.season.status === "settling") {
    return `<span>${escapeHtml(t("progress.seasonSettlingTitle"))}</span><strong>${escapeHtml(t("progress.seasonSettlingBody"))}</strong><p>${escapeHtml(
      t("hall.seasonSettlingHint"),
    )}</p>`;
  }
  if (state.season.status === "claimable") {
    const claimWindow = state.season.claimClosesAt === null
      ? configuredClaimWindow
      : formatServerDuration(state.season.claimClosesAt, serverNow, locale);
    return `<span>${escapeHtml(t("progress.rewardReadyTitle"))}</span><strong>${escapeHtml(
      t("progress.rewardReadyBody", {
        value: formatUsdtCents(state.season.projection.estimatedRewardUsdCents, locale, true),
      }),
    )}</strong><p>${escapeHtml(t("hall.seasonClaimHint", { stars: state.season.claimPriceStars, window: claimWindow }))}</p><button type="button" data-action="claim-season-reward">${escapeHtml(
      t("progress.rewardClaimStars", { stars: state.season.claimPriceStars }),
    )}</button>`;
  }
  if (state.season.status === "closed") {
    const expired = state.season.claimStatus === "expired";
    return `<span>${escapeHtml(t(expired ? "progress.rewardExpiredTitle" : "progress.rewardClaimedTitle"))}</span><strong>${escapeHtml(
      t(expired ? "progress.rewardExpiredBody" : "progress.rewardClaimedBody"),
    )}</strong><p>${escapeHtml(
      expired ? t("hall.seasonExpiredHint", { window: configuredClaimWindow }) : t("hall.seasonClosedHint"),
    )}</p>`;
  }
  return `<span>${escapeHtml(t("progress.activeSeasonTitle"))}</span><strong>${escapeHtml(
    t("progress.activeSeasonBody", { time: formatSeasonTime(state.season.endsAt, serverNow, locale) }),
  )}</strong><p>${escapeHtml(t("hall.seasonActiveHint"))}</p>`;
}

function communityCovenant(context: TemplateContext): string {
  const { state, t } = context;
  const quote = quoteCommunityFinalBonus(BigInt(state.account.available), {
    channel: state.telegram.channel.joined,
    chat: state.telegram.chat.joined,
  });
  const applied = state.season.communityBonus.status === "applied";
  const finalized = !["active", "scheduled"].includes(state.season.status);
  const eligible = applied || (state.season.status === "active" && quote.eligible);
  const bonus = applied
    ? state.season.communityBonus.appliedAmount
    : quote.bonus.toString();
  const missing = [
    state.telegram.channel.joined ? null : t("hall.communityChannelShort"),
    state.telegram.chat.joined ? null : t("hall.communityChatShort"),
  ].filter((value): value is string => value !== null);
  const action = (target: "channel" | "chat") => {
    const membership = state.telegram[target];
    if (membership.joined || !membership.available) return "";
    return `<div>${membership.joinUrl === null ? "" : `<a href="${escapeHtml(membership.joinUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(
      t(target === "channel" ? "hall.openChannel" : "hall.openChat"),
    )}</a>`}<button type="button" data-action="check-community" data-community="${target}">${escapeHtml(
      t("hall.checkMembership"),
    )}</button></div>`;
  };
  return `<section class="community-covenant${eligible ? " is-eligible" : ""}${applied ? " is-applied" : ""}" aria-labelledby="community-covenant-title">
    <header><span>${escapeHtml(t("hall.communityKicker"))}</span><strong>${escapeHtml(
      applied
        ? t("hall.communityBonusApplied", { value: formatCurrencyAmount(bonus, state.settings.locale) })
        : eligible
          ? t("hall.communityBonusPreview", { value: formatCurrencyAmount(bonus, state.settings.locale) })
          : t("hall.communityBonusRate"),
    )}</strong></header>
    <h2 id="community-covenant-title">${escapeHtml(t("hall.communityTitle"))}</h2>
    <p>${escapeHtml(t("hall.communityBody"))}</p>
    <aside><span>${escapeHtml(
      finalized && !applied ? (state.settings.locale === "ru" ? "Бонус не начислен: условия проверены при завершении сезона." : "No bonus was added: eligibility was checked at the end of the season.") : eligible ? t("hall.communityReady") : t("hall.communityMissing", { targets: missing.join(` ${t("common.and")} `) }),
    )}</span><small>${escapeHtml(t("hall.communityFinalCheck"))}</small></aside>
    ${eligible || finalized ? "" : `<footer>${action("channel")}${action("chat")}</footer>`}
  </section>`;
}

function seasonTasksV2(context: TemplateContext): string {
  const { state, t } = context;
  const taskCodes = state.progress.seasonal.assignmentCodes.filter((code) => {
    const metric = getSeasonTaskDefinition(code).metric;
    return metric !== "channel-membership" && metric !== "chat-membership";
  });
  const completedCount = taskCodes.filter(
    (code) => seasonTaskProgress(state, code).complete,
  ).length;
  const artForMetric = (metric: TaskMetric): TrophyArt => {
    if (metric === "arena-matches") return TROPHY_HALL_ART.achievements["arena-guardian"];
    if (metric === "confirmed-invites") return TROPHY_HALL_ART.achievements["makers-circle"];
    if (metric === "upgrade-purchases") return TROPHY_HALL_ART.achievements["light-architect"];
    if (metric === "channel-membership" || metric === "chat-membership") return TROPHY_HALL_ART.seasonCrown;
    return TROPHY_HALL_ART.achievements["master-rhythm"];
  };
  return `${communityCovenant(context)}<section class="trophy-season-quests" aria-labelledby="season-quests-title"><header><div><span>${escapeHtml(
    t("hall.seasonQuestsKicker"),
  )}</span><h2 id="season-quests-title">${escapeHtml(t("hall.seasonQuestsTitle"))}</h2></div><strong>${escapeHtml(
    t("hall.seasonQuestsProgress", {
      current: completedCount,
      total: taskCodes.length,
    }),
  )}</strong></header><ul class="season-quests">${taskCodes
    .map((code) => {
      const definition = getSeasonTaskDefinition(code);
      const progress = seasonTaskProgress(state, code);
      const bounded = progress.current > progress.target ? progress.target : progress.current;
      const claimed = state.progress.seasonal.claimedCodes.includes(code);
      const unlocked = seasonTaskUnlocked(state, code);
      const copy = taskCopy("seasonTask", code, definition.metric);
      const finalCheckPending = definition.claimPhase === "season-end";
      const percent = progress.target === 0n ? 100 : Number((bounded * 100n) / progress.target);
      return `<li class="season-quest${progress.complete ? " is-complete" : ""}${claimed ? " is-claimed" : ""}${unlocked ? "" : " is-locked"}" data-season-task="${escapeHtml(code)}">
        ${trophyArt(artForMetric(definition.metric), "season-quest__art")}
        <div class="season-quest__body"><header><small>${escapeHtml(t(copy.title))}</small><em>${escapeHtml(
          claimed
            ? t("hall.claimed")
            : t("hall.progressValue", {
                current: formatAmount(bounded.toString(), state.settings.locale),
                target: formatAmount(progress.target.toString(), state.settings.locale),
              }),
        )}</em></header><p>${escapeHtml(
          t(copy.body, {
            target: formatAmount(progress.target.toString(), state.settings.locale),
            threshold: formatCurrencyAmount(GAME_CONFIG.referrals.activationPrimaryThreshold, state.settings.locale),
          }),
        )}</p><div class="season-quest__meter" role="progressbar" aria-label="${escapeHtml(t(copy.title))}" aria-valuemin="0" aria-valuemax="${progress.target}" aria-valuenow="${bounded}"><i style="--trophy-progress:${percent / 100}"></i></div>
        <aside><span>${escapeHtml(t("hall.taskReward"))}</span>${currency(definition.reward, context)}${
          progress.complete && !claimed && unlocked
            ? finalCheckPending
              ? `<small>${escapeHtml(t("hall.finalCheck"))}</small>`
              : state.season.status === "active"
                ? `<button type="button" data-action="claim-season-task" data-task="${escapeHtml(code)}">${escapeHtml(t("hall.claimTask"))}</button>`
                : ""
            : ""
        }</aside></div>
      </li>`;
    })
    .join("")}</ul></section>`;
}

function tonUsdtPrizeStatusCopy(context: TemplateContext): string {
  const award = context.state.tonUsdtPrize.award;
  if (award === null || award.status === "locked") return context.t("hall.tonPrize.locked");
  if (award.status === "wallet-required") return context.t("hall.tonPrize.walletRequired");
  if (award.status === "ready") return context.t("hall.tonPrize.ready");
  if (award.status === "processing") return context.t("hall.tonPrize.processing");
  if (award.status === "paid") return context.t("hall.tonPrize.paid");
  if (award.status === "expired") return context.t("hall.tonPrize.expired");
  return context.t("hall.tonPrize.failed");
}

function tonUsdtPrizePoolV2(context: TemplateContext): string {
  const { state, t } = context;
  const campaign = state.tonUsdtPrize.campaign;
  if (campaign?.seasonCode !== state.season.code || campaign.fundingStatus === "draft") return "";
  const tiers = [...campaign.tiers].sort((left, right) => left.place - right.place);
  const leading = tiers.filter((tier) => tier.place <= 3);
  const remaining = tiers.filter((tier) => tier.place > 3);
  const award = state.tonUsdtPrize.award;
  const tier = (place: number, amountUsdtMicros: string): string => `<li data-place="${place}"><span>${escapeHtml(
    t("hall.tonPrize.place", { place }),
  )}</span><strong>${escapeHtml(formatUsdtMicros(amountUsdtMicros, state.settings.locale, 0))}</strong></li>`;
  return `<section class="ton-usdt-prizes${award === null ? "" : " has-award"}" data-ton-usdt-prizes data-funding="${campaign.fundingStatus}" aria-labelledby="ton-usdt-prizes-title">
    <header><div><span>${escapeHtml(t("hall.tonPrize.kicker"))}</span><h2 id="ton-usdt-prizes-title">${escapeHtml(
      t("hall.tonPrize.title"),
    )}</h2></div><em>${escapeHtml(t("hall.tonPrize.network"))}</em></header>
    <p>${escapeHtml(t("hall.tonPrize.body"))}</p>
    <ol class="ton-usdt-prizes__podium">${leading.map((item) => tier(item.place, item.amountUsdtMicros)).join("")}</ol>
    <ol class="ton-usdt-prizes__places" start="4">${remaining.map((item) => tier(item.place, item.amountUsdtMicros)).join("")}</ol>
    ${award === null ? "" : `<aside class="ton-usdt-prizes__award" aria-live="polite"><span>${escapeHtml(t("hall.tonPrize.winnerKicker"))}</span><strong>${escapeHtml(
      t("hall.tonPrize.winnerTitle", { place: award.place }),
    )}</strong><em>${escapeHtml(t("hall.tonPrize.winnerAmount", {
      value: formatUsdtMicros(award.amountUsdtMicros, state.settings.locale, 0),
    }))}</em><p>${escapeHtml(tonUsdtPrizeStatusCopy(context))}</p></aside>`}
    <footer><p>${escapeHtml(t("hall.tonPrize.rule"))}</p><button type="button" data-action="open-ton-usdt-prizes">${escapeHtml(
      t("hall.tonPrize.details"),
    )}</button></footer>
  </section>`;
}

export function renderTonUsdtPrizeInfoV2(state: GameState, t: Translator): string {
  const campaign = state.tonUsdtPrize.campaign;
  if (campaign?.seasonCode !== state.season.code || campaign.fundingStatus === "draft") return "";
  const tiers = [...campaign.tiers].sort((left, right) => left.place - right.place);
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}"></button>
    <section class="ton-usdt-prize-sheet" role="dialog" aria-modal="true" aria-labelledby="ton-usdt-prize-sheet-title" data-testid="ton-usdt-prize-sheet">
      <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}">${closeIcon()}</button>
      <header><span>${escapeHtml(t("hall.tonPrize.sheetKicker"))}</span><h2 id="ton-usdt-prize-sheet-title">${escapeHtml(
        t("hall.tonPrize.sheetTitle"),
      )}</h2><p>${escapeHtml(t("hall.tonPrize.sheetBody"))}</p></header>
      <ol class="ton-usdt-prize-sheet__distribution">${tiers.map((item) => `<li><span>${escapeHtml(
        t("hall.tonPrize.place", { place: item.place }),
      )}</span><strong>${escapeHtml(formatUsdtMicros(item.amountUsdtMicros, state.settings.locale, 0))}</strong></li>`).join("")}</ol>
      <ol class="ton-usdt-prize-sheet__steps"><li><span>${escapeHtml(t("hall.tonPrize.stepFinal"))}</span></li><li><span>${escapeHtml(
        t("hall.tonPrize.stepWallet"),
      )}</span></li><li><span>${escapeHtml(t("hall.tonPrize.stepPayout"))}</span></li></ol>
      <p class="ton-usdt-prize-sheet__tie">${escapeHtml(t("hall.tonPrize.tieRule"))}</p>
    </section>`;
}

function seasonV2(context: TemplateContext): string {
  const { state, serverNow, t } = context;
  const locale = state.settings.locale;
  const projectionVisible = state.season.projection.visible;
  const projectionRevealTime = state.season.projection.revealsAt === null
    ? t("common.soon")
    : formatServerDuration(state.season.projection.revealsAt, serverNow, locale);
  const hasPendingClaim = pendingSeasonClaim(state) !== null;
  const claimReady = hasPendingClaim || state.season.status === "claimable";
  const currentPlace = state.season.projection.rank;
  const seasonCountdownTarget = getSeasonCountdownTarget(state.season);
  const countdown = getSeasonCountdownParts(seasonCountdownTarget, serverNow);
  const seasonCountdownTitle = state.season.status === "scheduled"
    ? t("hall.seasonStartsIn")
    : state.season.status === "claimable"
      ? t("hall.claimClosesIn")
      : t("hall.distributionIn");
  return `<section class="trophy-season" data-season-state="${state.season.status}" aria-labelledby="trophy-season-title">
    ${claimReady ? `<header class="trophy-season-state" data-season-claim="true">${seasonStatus(context)}</header>` : ""}
    <div class="trophy-season-chronicle">
      <header id="trophy-season-title"><span>${escapeHtml(t("hall.worldSeason"))}</span><h2>${escapeHtml(
        seasonCountdownTitle,
      )}</h2></header>
      <div class="trophy-season-countdown" role="timer" aria-label="${escapeHtml(
        formatSeasonCountdown(seasonCountdownTarget, serverNow, locale),
      )}">
        <span><strong data-season-days>${countdown.days}</strong><small>${escapeHtml(t("hall.countdownDays"))}</small></span>
        <i aria-hidden="true">·</i>
        <span><strong data-season-hours>${countdown.hours}</strong><small>${escapeHtml(t("hall.countdownHours"))}</small></span>
        <i aria-hidden="true">·</i>
        <span><strong data-season-minutes>${countdown.minutes}</strong><small>${escapeHtml(t("hall.countdownMinutes"))}</small></span>
      </div>
      ${state.season.status === "scheduled" ? "" : `<div class="trophy-season-fund"><span>${escapeHtml(t("hall.prizeFund"))}</span><strong data-season-fund>${escapeHtml(
        formatUsdtCents(state.season.prizeFundUsdCents, locale),
      )}</strong><small>${escapeHtml(t("hall.prizeFundNote"))}</small></div>`}
    </div>
    ${claimReady || state.season.status === "active" ? "" : `<header class="trophy-season-state">${seasonStatus(context)}</header>`}
    ${projectionVisible ? `<div class="trophy-season-projection">
      <div class="trophy-season-projection__rank"><span>${escapeHtml(t("hall.seasonPlace"))}</span><strong data-season-rank>${escapeHtml(
        currentPlace === null ? t("hall.rankAfterFirstCarat") : t("progress.place", { place: currentPlace }),
      )}</strong><small>${escapeHtml(t("hall.ofParticipants", { count: formatAmount(String(state.season.projection.participantCount), locale) }))}</small></div>
      <div class="trophy-season-projection__share"><span>${escapeHtml(t("hall.seasonShare"))}</span><strong data-season-share>${escapeHtml(
        formatSharePpm(state.season.projection.sharePpm, locale),
      )}</strong><small>${escapeHtml(t("hall.shareOfFund"))}</small></div>
      <div class="trophy-season-projection__reward"><span>${escapeHtml(t("hall.seasonEstimate"))}</span><strong data-season-estimate>${escapeHtml(
        formatUsdtCents(state.season.projection.estimatedRewardUsdCents, locale, true),
      )}</strong><p>${escapeHtml(t("hall.seasonEstimateNote"))}</p></div>
    </div>` : `<div class="trophy-season-projection trophy-season-projection--warming" data-season-projection-pending>
      <div><span>${escapeHtml(t("hall.projectionPreparingKicker"))}</span><strong data-season-projection-countdown="hall">${escapeHtml(
        t("hall.projectionPreparing", { time: projectionRevealTime }),
      )}</strong><p>${escapeHtml(t("hall.projectionPreparingBody"))}</p></div>
    </div>`}
    ${tonUsdtPrizePoolV2(context)}
    ${leaderboardGate(locale)}
    ${seasonTasksV2(context)}
    <div class="trophy-season-rule"><span aria-hidden="true"></span><div><strong>${escapeHtml(
      t("hall.seasonRuleTitle"),
    )}</strong><p>${escapeHtml(t("progress.snapshotRule"))}</p></div></div>
    <footer class="trophy-season-history"><span>${escapeHtml(t("hall.previousSeason"))}</span><strong>${escapeHtml(
      state.profile.bestPlace === null ? t("hall.noPreviousResult") : t("hall.previousResult", { place: state.profile.bestPlace }),
    )}</strong></footer>
  </section>`;
}

function hallWorldSummary(context: TemplateContext, segment: ProgressSegment): string {
  const { state, serverNow, t } = context;
  if (segment === "today") {
    const completeCount = dailyGoalsCompletedCount(state);
    const reward = state.progress.daily.rewardClaimed
      ? state.progress.daily.claimedReward
      : dailyRouteReward(state).toString();
    return `<span>${escapeHtml(t("hall.worldToday"))}</span><h2 data-daily-hero-progress>${escapeHtml(
      t(state.progress.daily.rewardClaimed ? "hall.heroDailyClaimed" : "hall.heroDailyProgress", { current: completeCount }),
    )}</h2><p>${escapeHtml(
      state.progress.daily.rewardClaimed
        ? t("hall.rewardStored")
        : t("hall.heroDailyReward", { value: formatCurrencyAmount(reward, state.settings.locale) }),
    )}</p>`;
  }
  if (segment === "achievements") {
    return `<span>${escapeHtml(t("hall.worldCollection"))}</span><h2>${escapeHtml(
      t("hall.collectionCount", {
        current: collectionCatalogOwnedCount(state.collection.items),
        total: collectionCatalogTotal(state.collection.items),
      }),
    )}</h2><p>${escapeHtml(t("hall.collectionPermanent"))}</p>`;
  }
  const pendingClaim = pendingSeasonClaim(state);
  if (pendingClaim !== null) {
    return `<span>${escapeHtml(t("hall.worldSeason"))}</span><h2>${escapeHtml(
      t("progress.rewardReadyTitle"),
    )}</h2><p>${escapeHtml(
      t("progress.rewardReadyBody", {
        value: formatUsdtMicros(pendingClaim.referenceUsdtMicros, state.settings.locale),
      }),
    )}</p>`;
  }
  const seasonHeadline =
    state.season.status === "active"
      ? formatSeasonCountdown(state.season.endsAt, serverNow, state.settings.locale)
      : t(
          state.season.status === "scheduled"
            ? "progress.seasonScheduledTitle"
            : state.season.status === "frozen"
              ? "progress.seasonFrozenTitle"
              : state.season.status === "settling"
                ? "progress.seasonSettlingTitle"
              : state.season.status === "claimable"
                ? "progress.rewardReadyTitle"
                : "progress.rewardClaimedTitle",
        );
  return `<span>${escapeHtml(t("hall.worldSeason"))}</span><h2>${escapeHtml(seasonHeadline)}</h2><p>${escapeHtml(
    t("hall.seasonHeroRule"),
  )}</p>`;
}

export function renderProgressV2(
  state: GameState,
  segment: ProgressSegment,
  serverNow: string,
  t: Translator,
  achievementFilter: AchievementFilter,
): string {
  const context: TemplateContext = { state, serverNow, t };
  const segments: readonly ProgressSegment[] = ["today", "achievements", "season"];
  const completeCount = dailyGoalsCompletedCount(state);
  const readyRewards = readyRewardSummary(state);
  const segmentArt: Record<ProgressSegment, TrophyArt> = {
    today: TROPHY_HALL_ART.dailySeal,
    achievements: TROPHY_HALL_ART.achievements["living-facet"],
    season: TROPHY_HALL_ART.seasonCrown,
  };
  const segmentMeta: Record<ProgressSegment, string> = {
    today: readyRewards.daily > 0
      ? t("hall.portalDailyReady")
      : t("hall.portalTodayMeta", { current: completeCount }),
    achievements: readyRewards.achievements > 0
      ? t("hall.portalAchievementsReady", { count: readyRewards.achievements })
      : t("hall.portalCollectionMeta", { count: collectionCatalogOwnedCount(state.collection.items) }),
    season: readyRewards.season > 0 ? t("hall.portalSeasonReady") : t("hall.portalSeasonMeta"),
  };
  const segmentReadyCount: Record<ProgressSegment, number> = {
    today: readyRewards.daily,
    achievements: readyRewards.achievements,
    season: readyRewards.season,
  };
  const content = segment === "today" ? todayV2(context) : segment === "achievements" ? achievementsV2(context, achievementFilter) : seasonV2(context);
  return `<div class="realm-page realm-page--hall" data-testid="progress-screen" data-trophy-segment="${segment}">
    <section class="trophy-hall-hero" aria-label="${escapeHtml(t("progress.title"))}">
      <div class="trophy-hall-runtime" id="trophy-hall-runtime" data-testid="trophy-hall-runtime" style="background-image:url('${escapeHtml(
        TROPHY_HALL_ART.background.url,
      )}')" aria-hidden="true"></div>
      <div class="trophy-hall-hero__shade" aria-hidden="true"></div>
      <div class="trophy-hall-hero__identity">${hallWorldSummary(context, segment)}</div>
      <div class="trophy-hall-hero__crest" aria-hidden="true"><i></i><span></span><i></i></div>
    </section>
    <div class="trophy-hall-portals" role="tablist" aria-label="${escapeHtml(t("progress.title"))}">${segments
      .map(
        (item) => `<button type="button" role="tab" id="progress-tab-${item}" aria-controls="progress-panel" data-action="progress-segment" data-segment="${item}" aria-selected="${item === segment}" tabindex="${item === segment ? "0" : "-1"}">${trophyArt(
          segmentArt[item],
          "trophy-hall-portals__art",
        )}<span><strong>${escapeHtml(t(`progress.${item}`))}</strong><small>${escapeHtml(segmentMeta[item])}</small></span>${segmentReadyCount[item] > 0 ? `<em class="trophy-hall-portals__ready" aria-label="${escapeHtml(
          t("hall.portalReadyBadge", { count: segmentReadyCount[item] }),
        )}">${segmentReadyCount[item]}</em>` : ""}<i aria-hidden="true"></i></button>`,
      )
      .join("")}</div>
    <div class="trophy-hall-panel" role="tabpanel" id="progress-panel" aria-labelledby="progress-tab-${segment}">${content}</div>
  </div>`;
}

export function renderFriendsV2(
  state: GameState,
  t: Translator,
  filter: FriendFilter,
  query = "",
  page = 1,
  inviteUrl = "",
): string {
  const context: TemplateContext = { state, serverNow: state.meta.lastProductionAt, t };
  const networkCount = state.clubRoster.networkCount;
  const nextCount = Math.max(0, GAME_CONFIG.referrals.nextMilestoneMembers - networkCount);
  const nearCount = state.clubRoster.nearCount;
  const farCount = state.clubRoster.farCount;
  const filters: readonly [FriendFilter, TranslationKey, MakersClubArt, number][] = [
    ["all", "club.filterAllV2", MAKERS_CLUB_ART.beacon, networkCount],
    [1, "club.filterNearCompact", MAKERS_CLUB_ART.nearSignet, nearCount],
    [2, "club.filterFarCompact", MAKERS_CLUB_ART.farSignet, farCount],
  ];
  const heroBody =
    networkCount === 0
      ? t("club.worldEmptyBody")
      : nextCount === 0
        ? t("club.worldReady", { count: networkCount })
        : t("club.worldProgress", { count: networkCount, remaining: nextCount });
  const selectedTab = `club-tab-${filter}`;
  const milestoneProgress = Math.min(
    100,
    (networkCount / GAME_CONFIG.referrals.nextMilestoneMembers) * 100,
  );
  const firstRate = `${GAME_CONFIG.referrals.levelOneRateBasisPoints / 100}%`;
  const secondRate = `${GAME_CONFIG.referrals.levelTwoRateBasisPoints / 100}%`;
  const threshold = formatCurrencyAmount(
    GAME_CONFIG.referrals.activationPrimaryThreshold,
    state.settings.locale,
  );

  return `<div class="realm-page realm-page--makers-club" data-testid="friends-screen">
    <section class="makers-club-hero${networkCount === 0 ? " is-empty" : ""}" aria-label="${escapeHtml(t("friends.title"))}">
      <div class="makers-club-runtime" id="makers-club-runtime" data-testid="makers-club-runtime" style="background-image:url('${escapeHtml(
        MAKERS_CLUB_ART.background.url,
      )}')" aria-hidden="true"><span class="makers-club-runtime__loader"></span></div>
      <div class="makers-club-hero__shade" aria-hidden="true"></div>
      <div class="makers-club-hero__identity"><span>${escapeHtml(t("club.worldKicker"))}</span><h2>${escapeHtml(
        t(networkCount === 0 ? "club.worldEmptyTitle" : "club.worldTitle"),
      )}</h2><p>${escapeHtml(heroBody)}</p><button class="makers-club-invite" type="button" data-action="invite-friend" data-mode="share">${escapeHtml(
        t("club.inviteMaster"),
      )}</button></div>
    </section>
    <div class="makers-club-portals" role="tablist" aria-label="${escapeHtml(t("club.filterA11y"))}">${filters
      .map(
        ([value, label, art, count]) => `<button type="button" role="tab" id="club-tab-${value}" aria-controls="makers-club-register" data-action="friend-filter" data-filter="${value}" aria-selected="${filter === value}" aria-pressed="${filter === value}" tabindex="${filter === value ? "0" : "-1"}">${makersClubArt(
          art,
          "makers-club-portals__art",
        )}<span><strong>${escapeHtml(t(label))}</strong><small>${escapeHtml(t("club.filterCount", { count }))}</small></span><i aria-hidden="true"></i></button>`,
      )
      .join("")}</div>
    <div class="makers-club-panel">
      <div class="makers-club-support">
        <button class="partner-transfer-gateway" type="button" data-action="open-partner-transfer"><img src="${MAKERS_CLUB_ART.nearSignet.url}" alt="" width="54" height="54"/><span><strong>${state.settings.locale === "ru" ? "Передать мастеру" : "Support a master"}</strong><small>${state.settings.locale === "ru" ? "Помощь из счёта наград · ближний круг" : "Reward account support · first circle"}</small></span><b aria-hidden="true">›</b></button>
      </div>
      <section class="makers-club-sanctum" aria-labelledby="club-reward-title">
        <header class="makers-club-reward"><div><span>${escapeHtml(t("club.rewardKicker"))}</span><h2 id="club-reward-title">${escapeHtml(
          t("club.rewardTitle"),
        )}</h2><p>${escapeHtml(t("club.rewardNote"))}</p></div>${currency(state.referrals.rewardEarned, context)}</header>
        <dl class="makers-club-stats"><div><dt>${escapeHtml(t("club.members"))}</dt><dd>${networkCount}</dd></div><div><dt>${escapeHtml(
          t("club.registrationReward"),
        )}</dt><dd>${escapeHtml(formatCurrencyAmount(state.referrals.registrationRewardEarned, state.settings.locale))}</dd></div><div><dt>${escapeHtml(
          t("club.productionReward"),
        )}</dt><dd>${escapeHtml(formatCurrencyAmount(state.referrals.productionRewardEarned, state.settings.locale))}</dd></div></dl>
        <div class="makers-club-milestone"><div><span>${escapeHtml(t("club.milestone"))}</span><strong>${escapeHtml(
          t("club.milestoneProgress", {
            current: Math.min(networkCount, GAME_CONFIG.referrals.nextMilestoneMembers),
            target: GAME_CONFIG.referrals.nextMilestoneMembers,
          }),
        )}</strong></div><div class="makers-club-milestone__track" role="progressbar" aria-label="${escapeHtml(
          t("club.milestone"),
        )}" aria-valuemin="0" aria-valuemax="${GAME_CONFIG.referrals.nextMilestoneMembers}" aria-valuenow="${Math.min(
          networkCount,
          GAME_CONFIG.referrals.nextMilestoneMembers,
        )}"><i style="--club-progress:${milestoneProgress}%"></i></div><p>${escapeHtml(
          nextCount === 0 ? t("club.milestoneReady") : t("club.milestoneRemaining", { count: nextCount }),
        )}</p></div>
        <div class="makers-club-oath"><header><span>${escapeHtml(t("club.oathKicker"))}</span><h2>${escapeHtml(
          t("club.oathTitle", { first: firstRate, second: secondRate }),
        )}</h2></header><div class="makers-club-oath__circles">
          <button type="button" data-testid="club-near-rate" data-action="open-section-help" aria-label="${escapeHtml(
            t("club.nearRateA11y", { rate: firstRate, threshold }),
          )}">${makersClubArt(MAKERS_CLUB_ART.nearSignet, "makers-club-oath__art")}<div><span>${escapeHtml(
            t("club.filterNear"),
          )}</span><strong>${firstRate}</strong><p>${escapeHtml(
            t("club.nearBody", { rate: firstRate, threshold }),
          )}</p><small>${escapeHtml(t("club.rateDetails"))}<i aria-hidden="true">→</i></small></div></button>
          <button type="button" data-testid="club-far-rate" data-action="open-section-help" aria-label="${escapeHtml(
            t("club.farRateA11y", { rate: secondRate, threshold }),
          )}">${makersClubArt(MAKERS_CLUB_ART.farSignet, "makers-club-oath__art")}<div><span>${escapeHtml(
            t("club.filterFar"),
          )}</span><strong>${secondRate}</strong><p>${escapeHtml(
            t("club.farBody", { rate: secondRate, threshold }),
          )}</p><small>${escapeHtml(t("club.rateDetails"))}<i aria-hidden="true">→</i></small></div></button>
        </div><p class="makers-club-oath__threshold"><span aria-hidden="true">◆</span>${escapeHtml(
          t("club.thresholdRule"),
        )}</p><p class="makers-club-task-rule">${escapeHtml(t("club.taskRule"))}</p></div>
      </section>
      <section class="makers-club-register" id="makers-club-register" role="tabpanel" aria-labelledby="${selectedTab}">
        <header><div><span>${escapeHtml(t("club.registerKicker"))}</span><h2>${escapeHtml(
          t("club.registerTitle"),
        )}</h2></div><strong>${escapeHtml(t("club.registerTotal", { count: networkCount }))}</strong></header>
        ${
          networkCount === 0
            ? ""
            : `<label class="makers-club-search"><span class="visually-hidden">${escapeHtml(
                t("club.searchLabel"),
              )}</span><i aria-hidden="true"></i><input type="search" data-action="friend-search" data-testid="friend-search" value="${escapeHtml(
                query,
              )}" name="friend-search" maxlength="40" autocomplete="off" spellcheck="false" placeholder="${escapeHtml(
                t("club.searchPlaceholderV2"),
              )}" aria-label="${escapeHtml(t("club.searchLabel"))}"/></label>`
        }
        <div class="makers-club-register__results" data-club-roster-results>${renderFriendRosterV2(
          state,
          t,
          filter,
          query,
          page,
        )}</div>
      </section>
      <section class="makers-club-gate">
        ${makersClubArt(MAKERS_CLUB_ART.invitationKey, "makers-club-gate__art")}
        <div class="makers-club-gate__body"><span>${escapeHtml(t("club.inviteGateKicker"))}</span><h2>${escapeHtml(
          t("club.inviteGateTitle"),
        )}</h2><p>${escapeHtml(t("club.inviteGateBody"))}</p><output aria-label="${escapeHtml(
          t("club.inviteLinkA11y"),
        )}" title="${escapeHtml(inviteUrl)}">${escapeHtml(inviteUrl)}</output></div>
        <div class="makers-club-gate__actions"><button type="button" data-action="invite-friend" data-mode="share">${escapeHtml(
          t("club.inviteShare"),
        )}</button><button type="button" data-action="invite-friend" data-mode="copy">${escapeHtml(
          t("club.inviteCopy"),
        )}</button></div>
      </section>
    </div>
  </div>`;
}

type MakersClubArt = {
  url: string;
  width: number;
  height: number;
};

function makersClubArt(asset: MakersClubArt, className: string): string {
  return `<span class="${className}" aria-hidden="true"><img src="${escapeHtml(asset.url)}" alt="" width="${asset.width}" height="${asset.height}" loading="lazy" decoding="async" /></span>`;
}

export function renderFriendRosterV2(
  state: GameState,
  t: Translator,
  _filter: FriendFilter,
  _query = "",
  _page = 1,
): string {
  const pageInfo = state.clubRoster;
  if (pageInfo.networkCount === 0) {
    return `<div class="makers-club-empty">${makersClubArt(
      MAKERS_CLUB_ART.invitationKey,
      "makers-club-empty__art",
    )}<h2>${escapeHtml(t("club.emptyLeadV2"))}</h2><p>${escapeHtml(t("club.emptyBodyV2"))}</p><button type="button" data-action="invite-friend" data-mode="share">${escapeHtml(
      t("club.inviteMaster"),
    )}</button></div>`;
  }
  if (pageInfo.filteredCount === 0) {
    return `<div class="makers-club-no-results">${makersClubArt(
      MAKERS_CLUB_ART.memberMedallion,
      "makers-club-no-results__art",
    )}<h2>${escapeHtml(t("club.noResultsTitleV2"))}</h2><p>${escapeHtml(
      t("club.noResultsBodyV2"),
    )}</p><button type="button" data-action="clear-friend-search">${escapeHtml(t("club.clearSearchV2"))}</button></div>`;
  }

  const pageSize = pageInfo.pageSize;
  const totalPages = pageInfo.totalPages;
  const currentPage = pageInfo.page;
  const pageStart = (currentPage - 1) * pageSize;
  const visible = state.friends;
  const threshold = amount(GAME_CONFIG.referrals.activationPrimaryThreshold);
  const roster = visible
    .map((friend) => {
      const produced = amount(friend.primaryProduced);
      const remaining = produced >= threshold ? 0n : threshold - produced;
      const progress = threshold === 0n ? 1 : Math.min(1, Number((produced * 1_000n) / threshold) / 1_000);
      const active = remaining === 0n;
      return `<li class="makers-club-member${active ? " is-earning" : " is-awakening"}">
        <span class="makers-club-member__avatar" aria-hidden="true">${makersClubArt(
          MAKERS_CLUB_ART.memberMedallion,
          "makers-club-member__medallion",
        )}<b>${escapeHtml(friend.name.slice(0, 1))}</b></span>
        <div class="makers-club-member__identity"><strong>${escapeHtml(friend.name)}</strong><span>${escapeHtml(
          t(friend.line === 1 ? "club.filterNear" : "club.filterFar"),
        )}</span><div class="makers-club-member__meter" role="progressbar" aria-label="${escapeHtml(
          t("club.memberThresholdA11y", { name: friend.name }),
        )}" aria-valuemin="0" aria-valuemax="${GAME_CONFIG.referrals.activationPrimaryThreshold}" aria-valuenow="${produced > threshold ? threshold : produced}"><i style="--member-progress:${progress * 100}%"></i></div><small>${escapeHtml(
          active
            ? t(friend.line === 1 ? "club.memberTaskCounted" : "club.memberFarReward")
            : friend.seasonParticipant === false
              ? t("club.memberNotInSeason")
              : t(friend.seasonParticipant === true ? "club.memberInSeason" : "club.memberRemaining", { value: formatCurrencyAmount(remaining.toString(), state.settings.locale) }),
        )}</small></div>
        <dl class="makers-club-member__numbers"><div><dt>${escapeHtml(t("club.memberProducedV2"))}</dt><dd>${escapeHtml(
          formatAmount(friend.primaryProduced, state.settings.locale),
        )}</dd></div><div><dt>${escapeHtml(t("club.memberRewardV2"))}</dt><dd>+${escapeHtml(
          formatAmount(friend.rewardEarned, state.settings.locale),
        )}</dd></div></dl>
      </li>`;
    })
    .join("");
  const pageNumbers = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])]
    .filter((item) => item >= 1 && item <= totalPages)
    .sort((left, right) => left - right);
  let previousPageNumber = 0;
  const numberedPages = pageNumbers
    .map((item) => {
      const gap = previousPageNumber > 0 && item - previousPageNumber > 1
        ? `<span class="makers-club-pagination__gap" aria-hidden="true">…</span>`
        : "";
      previousPageNumber = item;
      return `${gap}<button type="button" data-action="friend-page" data-page="${item}" aria-label="${escapeHtml(
        t("club.pageA11y", { page: item }),
      )}" aria-current="${item === currentPage ? "page" : "false"}">${item}</button>`;
    })
    .join("");
  const pagination =
    totalPages <= 1
      ? ""
      : `<nav class="makers-club-pagination" aria-label="${escapeHtml(t("club.paginationA11y"))}">
          <button type="button" data-action="friend-page" data-page="${currentPage - 1}" aria-label="${escapeHtml(
            t("club.previousPage"),
          )}" ${currentPage === 1 ? "disabled" : ""}>←</button>
          ${numberedPages}
          <button type="button" data-action="friend-page" data-page="${currentPage + 1}" aria-label="${escapeHtml(
            t("club.nextPage"),
          )}" ${currentPage === totalPages ? "disabled" : ""}>→</button>
        </nav>`;
  return `<p class="makers-club-register__count">${escapeHtml(
    t("club.showing", {
      from: pageStart + 1,
      to: Math.min(pageStart + visible.length, pageInfo.filteredCount),
      total: pageInfo.filteredCount,
    }),
  )}</p><ul class="makers-club-members">${roster}</ul>${pagination}`;
}

export function renderEnergyV2(state: GameState, t: Translator, feedback: SpendFeedback | null = null): string {
  const context: TemplateContext = { state, serverNow: state.meta.lastEnergyAt, t };
  const missing = Math.max(0, state.energy.capacity - state.energy.current);
  const cost = quoteEnergyRefillCost(missing, state.account.tapReward, GAME_CONFIG.energy.refillPriceBasisPoints);
  const refillsLeft = Math.max(0, GAME_CONFIG.energy.dailyRefillLimit - state.energy.refillsUsedToday);
  const full = missing === 0;
  const limited = refillsLeft === 0;
  const canAfford = amount(state.account.available) >= amount(cost);
  const canRefill = feedback === null && !full && !limited && canAfford && state.season.status === "active";
  const fill = energyFillPercent(state.energy.current, state.energy.capacity);
  const action = feedback !== null ? t(feedback.phase === "saving" ? "spend.saving" : "spend.sending") : full
    ? t("energy.full")
    : limited
      ? t("energy.limitTitle")
      : t("energy.refillAction");

  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
    t("sheet.close"),
  )}"></button><section class="bottom-sheet energy-sheet" role="dialog" aria-modal="true" aria-labelledby="energy-sheet-title" aria-describedby="energy-sheet-lead">
    <header class="energy-sheet__header">
    <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("sheet.close"),
    )}">${closeIcon()}</button>
    <span class="sheet-kicker">${escapeHtml(t("energy.kicker"))}</span><h2 id="energy-sheet-title">${escapeHtml(
      t("energy.sheetTitle"),
    )}</h2><p id="energy-sheet-lead">${escapeHtml(
      full ? t("energy.fullBody") : t("energy.sheetLead", { value: formatAmount(String(missing), state.settings.locale) }),
    )}</p></header>
    <div class="energy-sheet__core" aria-hidden="true"><span class="energy-sheet__sigil" style="--energy-fill:${fill}%"></span></div>
    <div class="energy-sheet__reserve">
      <span><small>${escapeHtml(t("energy.reserve"))}</small><strong><b data-live-energy-current>${formatAmount(
        String(state.energy.current),
        state.settings.locale,
      )}</b> / <i data-live-energy-capacity>${formatAmount(String(state.energy.capacity), state.settings.locale)}</i></strong></span>
      <span><small>${escapeHtml(t("energy.recoveryRate"))}</small><strong>${escapeHtml(
        t("energy.recoveryRateValue", {
          value: formatEnergyRecoveryRate(state.energy.recoverySecondsPerUnit * 10000 / (state.energy.recoveryMultiplierBasisPoints ?? 10000), state.settings.locale),
        }),
      )}</strong></span>
    </div>
    <div class="energy-sheet__meter" role="progressbar" aria-label="${escapeHtml(t("energy.reserve"))}" aria-valuemin="0" aria-valuemax="${state.energy.capacity}" aria-valuenow="${state.energy.current}" data-live-energy-meter><i style="--energy-fill:${fill}%"></i></div>
    <div class="energy-sheet__terms"><span><small>${escapeHtml(t("energy.refillAmount"))}</small><strong>+${escapeHtml(
      formatAmount(String(missing), state.settings.locale),
    )}</strong></span><span><small>${escapeHtml(t("workshop.sheetCost"))}</small>${currency(cost, context, true)}</span><span><small>${escapeHtml(
      t("energy.today"),
    )}</small><strong>${refillsLeft} / ${GAME_CONFIG.energy.dailyRefillLimit}</strong></span></div>
    <button class="energy-sheet__action" type="button" data-action="refill-energy" ${feedback !== null ? 'aria-busy="true"' : ""} ${canRefill ? "" : "disabled"}><span>${escapeHtml(
      action,
    )}</span>${!full && !limited ? currency(cost, context, true) : ""}</button>
    ${renderSpendFeedback(feedback, t)}${
      !full && !limited && !canAfford
        ? `<p class="energy-sheet__message"><span aria-hidden="true">◇</span>${escapeHtml(
            t("workshop.shortfall", {
              value: formatCurrencyAmount((amount(cost) - amount(state.account.available)).toString(), state.settings.locale),
            }),
          )}</p>`
        : limited
          ? `<p class="energy-sheet__message"><span aria-hidden="true">↻</span>${escapeHtml(t("energy.limitBody"))}</p>`
          : `<p class="energy-sheet__message"><span aria-hidden="true">↻</span><span data-live-energy-status>${escapeHtml(
              energyStatus(state, t),
            )}</span></p>`
    }
  </section>`;
}

export function renderSectionHelpV2(
  state: GameState,
  t: Translator,
  section: Exclude<SectionId, "core">,
): string {
  if (section === "development") {
    return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("sheet.close"),
    )}"></button><section class="bottom-sheet section-help-sheet workshop-help-sheet" role="dialog" aria-modal="true" aria-labelledby="section-help-title" aria-describedby="workshop-help-lead">
      <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(
        t("sheet.close"),
      )}">${closeIcon()}</button>
      <header class="workshop-help-sheet__header">
        <span class="sheet-kicker">${escapeHtml(t("help.kicker"))}</span>
        <h2 id="section-help-title">${escapeHtml(t("help.workshop.title"))}</h2>
        <p id="workshop-help-lead">${escapeHtml(t("help.workshop.lead"))}</p>
      </header>
      <div class="workshop-help-tools">
        <article class="workshop-help-tool workshop-help-tool--mechanical">
          <span class="workshop-help-tool__art" aria-hidden="true"><i></i></span>
          <div><small>${escapeHtml(t("help.workshop.mechanicalLabel"))}</small><h3>${escapeHtml(
            t("help.workshop.mechanicalTitle"),
          )}</h3><p>${escapeHtml(t("help.workshop.mechanicalBody"))}</p></div>
        </article>
        <article class="workshop-help-tool workshop-help-tool--precision">
          <span class="workshop-help-tool__art" aria-hidden="true"><i></i></span>
          <div><small>${escapeHtml(t("help.workshop.precisionLabel"))}</small><h3>${escapeHtml(
            t("help.workshop.precisionTitle"),
          )}</h3><p>${escapeHtml(t("help.workshop.precisionBody"))}</p></div>
        </article>
        <article class="workshop-help-tool workshop-help-tool--capacity">
          <span class="workshop-help-tool__art" aria-hidden="true"><i></i></span>
          <div><small>${escapeHtml(t("help.workshop.capacityLabel"))}</small><h3>${escapeHtml(
            t("help.workshop.capacityTitle"),
          )}</h3><p>${escapeHtml(t("help.workshop.capacityBody"))}</p></div>
        </article>
        <article class="workshop-help-tool workshop-help-tool--recovery">
          <span class="workshop-help-tool__art" aria-hidden="true"><i></i></span>
          <div><small>${escapeHtml(t("help.workshop.recoveryLabel"))}</small><h3>${escapeHtml(
            t("help.workshop.recoveryTitle"),
          )}</h3><p>${escapeHtml(t("help.workshop.recoveryBody"))}</p></div>
        </article>
      </div>
      <button class="workshop-help-sheet__action" type="button" data-action="close-sheet">${escapeHtml(
        t("help.workshop.action"),
      )}</button>
    </section>`;
  }

  if (section === "friends") {
    const first = `${GAME_CONFIG.referrals.levelOneRateBasisPoints / 100}%`;
    const second = `${GAME_CONFIG.referrals.levelTwoRateBasisPoints / 100}%`;
    const thresholdValue = amount(GAME_CONFIG.referrals.activationPrimaryThreshold);
    const exampleProductionValue = thresholdValue * 2n;
    const exampleEligibleValue = exampleProductionValue - thresholdValue;
    const firstRewardValue =
      (exampleEligibleValue * BigInt(GAME_CONFIG.referrals.levelOneRateBasisPoints)) / 10_000n;
    const secondRewardValue =
      (exampleEligibleValue * BigInt(GAME_CONFIG.referrals.levelTwoRateBasisPoints)) / 10_000n;
    const threshold = formatCurrencyAmount(thresholdValue.toString(), state.settings.locale);
    const exampleProduction = formatCurrencyAmount(exampleProductionValue.toString(), state.settings.locale);
    const exampleEligible = formatCurrencyAmount(exampleEligibleValue.toString(), state.settings.locale);
    const exampleProductionNumber = formatAmount(exampleProductionValue.toString(), state.settings.locale);
    const thresholdNumber = formatAmount(thresholdValue.toString(), state.settings.locale);
    const exampleEligibleNumber = formatAmount(exampleEligibleValue.toString(), state.settings.locale);
    const registrationReward = formatCurrencyAmount(
      GAME_CONFIG.referrals.confirmedRegistrationReward,
      state.settings.locale,
    );
    const context: TemplateContext = { state, serverNow: state.meta.lastProductionAt, t };
    return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("sheet.close"),
    )}"></button><section class="bottom-sheet section-help-sheet club-help-sheet" role="dialog" aria-modal="true" aria-labelledby="section-help-title" aria-describedby="club-help-lead">
      <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(
        t("sheet.close"),
      )}">${closeIcon()}</button>
      <header class="club-help-sheet__header"><span class="sheet-kicker">${escapeHtml(
        t("help.club.kickerV2"),
      )}</span><h2 id="section-help-title">${escapeHtml(t("help.club.titleV2", { first, second }))}</h2><p id="club-help-lead">${escapeHtml(
        t("help.club.leadV2", { threshold }),
      )}</p><p class="club-help-registration"><span aria-hidden="true">✦</span>${escapeHtml(
        t("help.club.registration", { value: registrationReward }),
      )}</p></header>
      <section class="club-help-example" aria-labelledby="club-help-example-title" data-testid="club-reward-example">
        <header><span>${escapeHtml(t("help.club.exampleKicker"))}</span><h3 id="club-help-example-title">${escapeHtml(
          t("help.club.exampleTitle", { production: exampleProduction }),
        )}</h3></header>
        <div class="club-help-example__equation" role="img" aria-label="${escapeHtml(
          t("help.club.exampleEquationA11y", {
            production: exampleProduction,
            threshold,
            eligible: exampleEligible,
          }),
        )}"><span>${escapeHtml(exampleProductionNumber)}</span><i aria-hidden="true">−</i><span>${escapeHtml(
          thresholdNumber,
        )}</span><i aria-hidden="true">=</i><strong>${escapeHtml(exampleEligibleNumber)}</strong></div>
        <p>${escapeHtml(t("help.club.exampleEligible", { eligible: exampleEligible }))}</p>
        <dl class="club-help-example__results">
          <div><dt><span>${escapeHtml(t("help.club.exampleNearLabel"))}</span><small>${escapeHtml(
            t("help.club.exampleFormula", { eligible: exampleEligibleNumber, rate: first }),
          )}</small></dt><dd><span class="club-help-example__reward"><i aria-hidden="true">+</i>${currency(
            firstRewardValue.toString(),
            context,
          )}</span><small>${escapeHtml(
            t("help.club.exampleToYou"),
          )}</small></dd></div>
          <div><dt><span>${escapeHtml(t("help.club.exampleFarLabel"))}</span><small>${escapeHtml(
            t("help.club.exampleFormula", { eligible: exampleEligibleNumber, rate: second }),
          )}</small></dt><dd><span class="club-help-example__reward"><i aria-hidden="true">+</i>${currency(
            secondRewardValue.toString(),
            context,
          )}</span><small>${escapeHtml(
            t("help.club.exampleToYou"),
          )}</small></dd></div>
        </dl>
        <p class="club-help-example__keeps">${escapeHtml(
          t("help.club.exampleKeeps", { production: exampleProduction }),
        )}</p>
      </section>
      <div class="club-help-circles">
        <article>${makersClubArt(MAKERS_CLUB_ART.nearSignet, "club-help-circle__art")}<div><span>${escapeHtml(
          t("club.filterNear"),
        )}</span><strong>${first}</strong><p>${escapeHtml(t("help.club.nearBody", { rate: first, threshold }))}</p></div></article>
        <article>${makersClubArt(MAKERS_CLUB_ART.farSignet, "club-help-circle__art")}<div><span>${escapeHtml(
          t("club.filterFar"),
        )}</span><strong>${second}</strong><p>${escapeHtml(t("help.club.farBody", { rate: second, threshold }))}</p></div></article>
      </div>
      <div class="club-help-ledger"><article><span aria-hidden="true"><i>✓</i></span><div><h3>${escapeHtml(
        t("help.club.countsTitle"),
      )}</h3><p>${escapeHtml(t("help.club.countsBody"))}</p></div></article><article><span aria-hidden="true"><i>×</i></span><div><h3>${escapeHtml(
        t("help.club.excludesTitle"),
      )}</h3><p>${escapeHtml(t("help.club.excludesBody"))}</p></div></article></div>
      <p class="club-help-sheet__note"><span aria-hidden="true">◆</span>${escapeHtml(t("help.club.noteV2", { first, second }))}</p>
      <button class="club-help-sheet__action" type="button" data-action="close-sheet">${escapeHtml(
        t("help.club.action"),
      )}</button>
    </section>`;
  }

  const titleKey: Record<Exclude<SectionId, "core" | "development">, TranslationKey> = {
    progress: "help.hall.title",
    friends: "help.club.title",
    games: "help.arena.title",
  };
  const leadKey: Record<Exclude<SectionId, "core" | "development">, TranslationKey> = {
    progress: "help.hall.lead",
    friends: "help.club.lead",
    games: "help.arena.lead",
  };
  const ruleKeys: Record<Exclude<SectionId, "core" | "development">, readonly TranslationKey[]> = {
    progress: ["help.hall.rule1", "help.hall.rule2", "help.hall.rule3"],
    friends: ["help.club.rule1", "help.club.rule2", "help.club.rule3", "help.club.rule4"],
    games: ["help.arena.rule1", "help.arena.rule2", "help.arena.rule3"],
  };
  const values = {
    first: `${GAME_CONFIG.referrals.levelOneRateBasisPoints / 100}%`,
    second: `${GAME_CONFIG.referrals.levelTwoRateBasisPoints / 100}%`,
    threshold: formatCurrencyAmount(GAME_CONFIG.referrals.activationPrimaryThreshold, state.settings.locale),
    minutes: GAME_CONFIG.balanceModel.targetDailyMinutes,
    hours: GAME_CONFIG.balanceModel.targetPassiveHours,
    stake: formatCurrencyAmount(GAME_CONFIG.pvp.stake, state.settings.locale),
    turnSeconds: Math.round(GAME_CONFIG.pvp.turnMs / 1_000),
    searchSeconds: Math.round(GAME_CONFIG.pvp.matchmakingMs / 1_000),
  };
  const noteKey: TranslationKey = section === "games" ? "help.arena.note" : "help.seasonNote";
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
    t("sheet.close"),
  )}"></button><section class="bottom-sheet section-help-sheet" role="dialog" aria-modal="true" aria-labelledby="section-help-title">
    <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("sheet.close"),
    )}">${closeIcon()}</button>
    <span class="sheet-kicker">${escapeHtml(t("help.kicker"))}</span>
    <h2 id="section-help-title">${escapeHtml(t(titleKey[section]))}</h2>
    <p class="section-help-sheet__lead">${escapeHtml(t(leadKey[section], values))}</p>
    <ol class="section-help-sheet__rules">${ruleKeys[section]
      .map(
        (key, index) => `<li><span aria-hidden="true">${index + 1}</span><p>${escapeHtml(t(key, values))}</p></li>`,
      )
      .join("")}</ol>
    <p class="section-help-sheet__note"><span aria-hidden="true">◇</span>${escapeHtml(
      t(noteKey, values),
    )}</p>
  </section>`;
}

export function renderArenaRulesV2(state: GameState, t: Translator): string {
  const stake = formatCurrencyAmount(GAME_CONFIG.pvp.stake, state.settings.locale);
  const bank = formatCurrencyAmount((amount(GAME_CONFIG.pvp.stake) * 2n).toString(), state.settings.locale);
  const values = {
    stake,
    bank,
    turnSeconds: Math.round(GAME_CONFIG.pvp.turnMs / 1_000),
  };
  const rules: readonly TranslationKey[] = [
    "help.penalty.rule1",
    "help.penalty.rule2",
    "help.penalty.rule3",
  ];
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
    t("sheet.close"),
  )}"></button><section class="bottom-sheet section-help-sheet arena-rules-sheet" role="dialog" aria-modal="true" aria-labelledby="arena-rules-title" aria-describedby="arena-rules-lead">
    <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("sheet.close"),
    )}">${closeIcon()}</button>
    <span class="sheet-kicker">${escapeHtml(t("help.penalty.kicker"))}</span>
    <h2 id="arena-rules-title">${escapeHtml(t("games.penalty"))}</h2>
    <p id="arena-rules-lead" class="section-help-sheet__lead">${escapeHtml(t("help.penalty.lead"))}</p>
    <ol class="section-help-sheet__rules">${rules
      .map((key, index) => `<li><span aria-hidden="true">${index + 1}</span><p>${escapeHtml(t(key, values))}</p></li>`)
      .join("")}</ol>
    <p class="section-help-sheet__note"><span aria-hidden="true">◇</span>${escapeHtml(
      t("help.penalty.stake", values),
    )}</p>
    <button class="arena-rules-sheet__action" type="button" data-action="close-sheet">${escapeHtml(
      t("help.penalty.action"),
    )}</button>
  </section>`;
}

export function renderOfflineRewardV2(
  state: GameState,
  t: Translator,
  gain: string,
  offlineSeconds: number,
): string {
  const amountValue = formatAmount(gain, state.settings.locale);
  const unit = formatCurrencyUnit(gain, state.settings.locale);
  const duration = formatOfflineDuration(offlineSeconds, state.settings.locale);
  return `<button class="modal-backdrop offline-reward-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
    t("sheet.close"),
  )}"></button><section class="offline-reward-dialog" data-testid="offline-reward" data-reward-ready="false" role="dialog" aria-modal="true" aria-labelledby="offline-reward-title" aria-describedby="offline-reward-body">
    <div class="offline-reward-poster" style="background-image:url('${escapeHtml(OFFLINE_REWARD_ART.background.url)}')" aria-hidden="true"></div>
    <div class="offline-reward-stage" id="offline-reward-stage" aria-hidden="true"></div>
    <div class="offline-reward-loader" data-testid="offline-reward-loader" data-state="loading" role="status" aria-live="polite">
      <div class="offline-reward-loader__gem" aria-hidden="true">${currencyIcon()}</div>
      <strong data-offline-loading-title>${escapeHtml(t("offline.loadingTitle"))}</strong>
      <span data-offline-loading-body>${escapeHtml(t("offline.loadingBody"))}</span>
      <div class="offline-reward-loader__progress" role="progressbar" aria-label="${escapeHtml(
        t("offline.loadingProgress"),
      )}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><i></i></div>
      <button type="button" data-action="retry-offline-reward" hidden>${escapeHtml(t("offline.loadingRetry"))}</button>
    </div>
    <button class="icon-button close-button offline-reward-dialog__close" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("sheet.close"),
    )}">${closeIcon()}</button>
    <header class="offline-reward-dialog__header">
      <span>${escapeHtml(t("offline.kicker"))}</span>
      <h2 id="offline-reward-title">${escapeHtml(t("offline.title"))}</h2>
      <p id="offline-reward-body">${escapeHtml(t("offline.body"))}</p>
    </header>
    <div class="offline-reward-dialog__yield">
      <span>${escapeHtml(t("offline.earned"))}</span>
      <div><b aria-hidden="true">+</b><strong>${escapeHtml(amountValue)}</strong><small>${escapeHtml(unit)}</small></div>
      <p>${escapeHtml(t("offline.duration", { duration }))}</p>
    </div>
    <button class="offline-reward-dialog__action" type="button" data-action="collect-offline-reward" disabled>${escapeHtml(
      t("offline.action"),
    )}</button>
  </section>`;
}

const premiumRelicArt = {
  reserve: PREMIUM_SHOP_ART.offlineReserve,
  booster: PREMIUM_SHOP_ART.passiveBooster,
  skin: PREMIUM_SHOP_ART.auroraSkin,
  signet: PREMIUM_SHOP_ART.seasonSignet,
} as const;

function premiumSkinArt(skinId: DiamondSkinId | null) {
  return skinId === null ? PREMIUM_SHOP_ART.auroraSkin : PREMIUM_SHOP_ART.skins[skinId];
}

function premiumOfferRelic(offerId: PremiumOfferId): PremiumShopRelic {
  const kind = premiumOfferById(offerId).kind;
  if (kind === "offline-reserve") return "reserve";
  if (kind === "passive-boost" || kind === "tap-boost" || kind === "energy-recovery-boost") return "booster";
  if (kind === "cosmetic") return "skin";
  return "signet";
}

function premiumOfferArt(offerId: PremiumOfferId) {
  const offer = premiumOfferById(offerId);
  if (offer.kind === "cosmetic" && offer.collectibleId !== undefined) {
    return PREMIUM_SHOP_ART.skins[offer.collectibleId as DiamondSkinId];
  }
  return premiumRelicArt[premiumOfferRelic(offerId)];
}

function premiumOfferCopy(
  offer: PremiumOfferConfig,
  state: GameState,
  t: Translator,
): { name: string; body: string } {
  const authoredCopyId = PREMIUM_OFFER_COPY_IDS.has(offer.id)
    ? offer.id
    : offer.collectibleId !== undefined && PREMIUM_OFFER_COPY_IDS.has(offer.collectibleId)
      ? offer.collectibleId
      : null;
  if (authoredCopyId !== null) {
    return {
      name: t(`shop.offer.${authoredCopyId}.name` as TranslationKey),
      body: t(`shop.offer.${authoredCopyId}.body` as TranslationKey),
    };
  }

  const durationSeconds = offer.offlineReserveSeconds ?? offer.durationSeconds ?? 0;
  const duration = formatOfflineDuration(durationSeconds, state.settings.locale);
  const percent = new Intl.NumberFormat(state.settings.locale === "ru" ? "ru-RU" : "en-US", {
    maximumFractionDigits: 2,
  }).format(Math.max(0, (offer.multiplierBasisPoints ?? 10_000) - 10_000) / 100);
  if (offer.kind === "offline-reserve") {
    return {
      name: t("shop.offer.generic.offline-reserve.name"),
      body: t("shop.offer.generic.offline-reserve.body", { duration }),
    };
  }
  if (offer.kind === "passive-boost") {
    return {
      name: t("shop.offer.generic.passive-boost.name"),
      body: t("shop.offer.generic.passive-boost.body", { duration, percent }),
    };
  }
  if (offer.kind === "tap-boost") {
    return {
      name: t("shop.offer.generic.tap-boost.name"),
      body: t("shop.offer.generic.tap-boost.body", { duration, percent }),
    };
  }
  throw new Error(`Premium collectible ${offer.id} has no released presentation`);
}

function energyBoostOfferCopy(offer: PremiumOfferConfig, state: GameState): { name: string; body: string } {
  const duration = formatOfflineDuration(offer.durationSeconds ?? 0, state.settings.locale);
  const percent = ((offer.multiplierBasisPoints ?? 10000) - 10000) / 100;
  return state.settings.locale === "ru" ? { name: "Пульс энергии", body: `Энергия восстанавливается на ${percent}% быстрее · ${duration}` }
    : { name: "Energy pulse", body: `Energy recovers ${percent}% faster for ${duration}.` };
}

function renderPremiumShopContextV2(
  state: GameState,
  serverNow: string,
  t: Translator,
  selectedRelic: PremiumShopRelic,
): string {
  if (selectedRelic === "reserve" && state.premium.offlineReserveSeconds >= 60) {
    return `<p class="premium-shop-context"><span aria-hidden="true">⌛</span>${escapeHtml(t("shop.reserveAvailable", {
      time: formatOfflineDuration(state.premium.offlineReserveSeconds, state.settings.locale),
    }))}</p>`;
  }
  if (selectedRelic === "booster") {
    const now = new Date(serverNow).getTime();
    const tapStatus = renderTapBoostStatus(state, now, t, true);
    const recoveryBoosts = (state.premium.energyRecoveryBoosts ?? []).filter(b => Date.parse(b.endsAt) > now);
    const recovery = recoveryBoosts.find(b => Date.parse(b.startsAt) <= now);
    const energyText = recovery ? (state.settings.locale === "ru" ? `Восстановление энергии +${(recovery.multiplierBasisPoints - 10000) / 100}% · ` : `Energy recovery +${(recovery.multiplierBasisPoints - 10000) / 100}% · `) + formatServerDuration(recovery.endsAt, serverNow, state.settings.locale)
      : recoveryBoosts.length ? (state.settings.locale === "ru" ? `Усилений энергии в очереди: ${recoveryBoosts.length}` : `Energy boosts queued: ${recoveryBoosts.length}`) : "";
    const tapBlock = `<div class="premium-shop-context premium-tap-status" data-tap-boost-status${tapStatus ? "" : " hidden"}>${tapStatus}</div>${energyText ? `<p class="premium-shop-context">${escapeHtml(energyText)}</p>` : ""}`;
    const boosts = state.premium.passiveBoosts.filter((boost) => Date.parse(boost.endsAt) > now);
    const active = boosts.find((boost) => new Date(boost.startsAt).getTime() <= now && now < new Date(boost.endsAt).getTime());
    if (active !== undefined) {
      return `${tapBlock}<p class="premium-shop-context"><span aria-hidden="true">✦</span>${escapeHtml(t("boost.passiveActive", {
        time: formatServerDuration(active.endsAt, serverNow, state.settings.locale),
      }))}</p>`;
    }
    if (boosts.length > 0) {
      return `${tapBlock}<p class="premium-shop-context"><span aria-hidden="true">✦</span>${escapeHtml(t("shop.queued", {
        count: boosts.length,
      }))}</p>`;
    }
    return tapBlock;
  }
  if (selectedRelic === "signet") {
    const offer = GAME_CONFIG.premium.offers.find((candidate) => candidate.kind === "tap-production-grant");
    if (offer !== undefined) {
      const availability = premiumOfferAvailability(offer, new Date(serverNow), state.season.startsAt);
      const countdown = availability.endsAt === null
        ? ""
        : formatServerDuration(availability.endsAt, serverNow, state.settings.locale);
      const status = availability.phase === "active"
        ? t("shop.startWindow.active", { time: countdown })
        : availability.phase === "upcoming"
          ? t("shop.startWindow.upcoming")
          : t("shop.startWindow.expired");
      return `<section class="premium-start-window" data-premium-start-phase="${availability.phase}"${availability.endsAt === null
        ? ""
        : ` data-premium-start-expires-at="${escapeHtml(availability.endsAt)}"`}>
        <div><span>${escapeHtml(t("shop.startWindow.kicker"))}</span><h3>${escapeHtml(t("shop.startWindow.title"))}</h3></div>
        <strong data-premium-start-countdown>${escapeHtml(status)}</strong>
        <p>${escapeHtml(t("shop.startWindow.base", {
          value: formatCurrencyAmount(confirmedTapProduction(state).toString(), state.settings.locale),
        }))}</p>
      </section>`;
    }
  }
  return "";
}

function renderClassicDiamondSkin(state: GameState, t: Translator): string {
  const selected = state.collection.selectedSkinId === null;
  const art = FACET_QUARTER_ART.diamond.base;
  return `<article class="premium-offer premium-offer--skin${selected ? " is-selected" : " is-owned"}" data-skin="default">
    <div class="premium-offer__art" aria-hidden="true"><img src="${escapeHtml(art.url)}" alt="" width="${art.width}" height="${art.height}"/></div>
    <div class="premium-offer__body"><header><span>${escapeHtml(t("shop.inCollection"))}</span></header><h3>${escapeHtml(
      t("shop.skin.default.name"),
    )}</h3><p>${escapeHtml(t("shop.skin.default.body"))}</p></div>
    <button type="button" data-action="select-diamond-skin" data-skin="" ${selected ? "disabled" : ""}>${escapeHtml(
      selected ? t("shop.selected") : t("shop.wear"),
    )}</button>
  </article>`;
}

export function renderPremiumShopContentV2(
  state: GameState,
  serverNow: string,
  t: Translator,
  selectedRelic: PremiumShopRelic,
  previewSkinId: DiamondSkinId | null = null,
): string {
  const now = new Date(serverNow);
  const offers = GAME_CONFIG.premium.offers.filter((offer) => premiumOfferRelic(offer.id) === selectedRelic);
  const noteKey: TranslationKey = selectedRelic === "reserve"
    ? "shop.reserveNote"
    : selectedRelic === "skin"
      ? "shop.collectionNote"
      : selectedRelic === "signet"
        ? "shop.startGrantNote"
        : "shop.starsNote";
  return `<nav class="premium-shop-relics" aria-label="${escapeHtml(t("shop.categories"))}">${(
      Object.keys(premiumRelicArt) as PremiumShopRelic[]
    )
      .map(
        (relic) => {
          const art = relic === "skin" ? premiumSkinArt(state.collection.selectedSkinId) : premiumRelicArt[relic];
          return `<button type="button" data-action="select-shop-relic" data-relic="${relic}" aria-pressed="${
          relic === selectedRelic
        }"><img src="${escapeHtml(art.url)}" alt="" width="${art.width}" height="${art.height}"/><span>${escapeHtml(
          t(`shop.category.${relic}` as TranslationKey),
        )}</span></button>`;
        },
      )
      .join("")}</nav>${renderPremiumShopContextV2(state, serverNow, t, selectedRelic)}
    <div class="premium-shop-offers" data-shop-relic="${selectedRelic}">${selectedRelic === "skin" ? renderClassicDiamondSkin(state, t) : ""}${offers
      .map((offer) => {
        const availability = premiumOfferAvailability(offer, now, state.season.startsAt);
        const remoteOffer = state.premium.offerStates?.[offer.id];
        const available = availability.active && remoteOffer?.available !== false;
        const purchases = remoteOffer?.purchasedCount ?? (state.premium.offerStates === undefined
          ? state.premium.purchasedOfferIds.filter((id) => id === offer.id).length : null);
        const owned = offer.collectibleId !== undefined && state.collection.items.some((item) => item.id === offer.collectibleId);
        const limitReached = offer.purchaseLimit !== null && purchases !== null && purchases >= offer.purchaseLimit;
        const sold = owned || limitReached;
        const consumable = offer.kind !== "cosmetic" && offer.collectibleId === undefined;
        const limitCopy = consumable && offer.purchaseLimit !== null
          ? `<p class="premium-offer__limit${limitReached ? " is-exhausted" : ""}" data-purchase-limit><span>${escapeHtml(purchases === null ? t("shop.purchaseLimit", { limit: offer.purchaseLimit }) : t("shop.purchaseCount", { count: purchases, limit: offer.purchaseLimit }))}</span>${limitReached ? `<strong>${escapeHtml(t("shop.limitReachedHint"))}</strong>` : ""}</p>`
          : "";
        const selected = offer.kind === "cosmetic" && state.collection.selectedSkinId === offer.collectibleId;
        const previewed = offer.kind === "cosmetic" && previewSkinId === offer.collectibleId;
        const timing = availability.endsAt === null
          ? t("shop.permanent")
          : available
            ? t("shop.endsIn", { time: formatServerDuration(availability.endsAt, serverNow, state.settings.locale) })
            : availability.phase === "upcoming"
              ? t("shop.opensWithSeason")
              : t("shop.finished");
        const art = premiumOfferArt(offer.id);
        const copy = offer.kind === "energy-recovery-boost" ? energyBoostOfferCopy(offer, state) : premiumOfferCopy(offer, state, t);
        const grant = offer.kind === "tap-production-grant"
          ? quoteTapProductionGrant(confirmedTapProduction(state), offer.grantBasisPoints ?? 0)
          : 0n;
        const grantPercent = offer.kind === "tap-production-grant"
          ? `${Math.max(0, offer.grantBasisPoints ?? 0) / 100}%`
          : "";
        const grantQuote = offer.kind === "tap-production-grant"
          ? `<div class="premium-offer__grant" data-premium-grant-quote="${escapeHtml(offer.id)}"><span>${escapeHtml(
            t(grant > 0n ? "shop.startWindow.grantNow" : "shop.startWindow.grantEmpty"),
          )}</span>${grant > 0n ? `<strong>+${escapeHtml(formatAmount(grant.toString(), state.settings.locale))}</strong>` : ""}<small>${escapeHtml(t("shop.startWindow.base", { value: formatAmount(confirmedTapProduction(state).toString(), state.settings.locale) }))}</small></div>`
          : "";
        const primaryAction = offer.kind === "cosmetic" && owned ? "select-diamond-skin" : "purchase-premium-offer";
        const primaryData = offer.kind === "cosmetic" && owned
          ? `data-skin="${escapeHtml(offer.collectibleId ?? "")}"`
          : `data-offer="${escapeHtml(offer.id)}"`;
        const primaryLabel = offer.kind === "cosmetic" && owned
          ? selected ? t("shop.selected") : t("shop.wear")
          : sold ? t(consumable && limitReached ? "shop.limitReached" : "shop.owned") : t("shop.buy", { stars: offer.priceStars });
        const previewAction = offer.kind === "cosmetic" && !owned && offer.collectibleId !== undefined
          ? `<button class="premium-offer__preview${previewed ? " is-active" : ""}" type="button" data-action="preview-diamond-skin" data-skin="${escapeHtml(
            offer.collectibleId,
          )}" aria-pressed="${previewed}">${escapeHtml(t(previewed ? "shop.previewing" : "shop.preview"))}</button>`
          : "";
        return `<article class="premium-offer${offer.kind === "cosmetic" ? " premium-offer--skin" : ""}${offer.kind === "tap-production-grant" ? " premium-offer--grant" : ""}${offer.featured ? " is-featured" : ""}${sold ? " is-owned" : ""}${selected ? " is-selected" : ""}" data-offer="${escapeHtml(offer.id)}"${availability.endsAt === null ? "" : ` data-premium-offer-expires-at="${escapeHtml(availability.endsAt)}"`}>
          <div class="premium-offer__art" aria-hidden="true"><img src="${escapeHtml(art.url)}" alt="" width="${art.width}" height="${art.height}"/>${grantPercent === "" ? "" : `<b>${escapeHtml(grantPercent)}</b>`}</div>
          <div class="premium-offer__body"><header><span>${escapeHtml(timing)}</span>${offer.featured ? `<em>${escapeHtml(t("shop.featured"))}</em>` : ""}</header><h3>${escapeHtml(
            copy.name,
          )}</h3><p>${escapeHtml(copy.body)}</p>${grantQuote}</div>
          <div class="premium-offer__actions">${previewAction}<button type="button" data-action="${primaryAction}" ${primaryData} ${selected || (!owned && (!available || sold || (offer.kind === "tap-production-grant" && grant <= 0n))) ? "disabled" : ""}>${escapeHtml(primaryLabel)}</button></div>
          ${limitCopy}
        </article>`;
      })
      .join("")}<p class="premium-shop-note"><span aria-hidden="true">✦</span>${escapeHtml(t(noteKey))}</p></div>`;
}

export function renderPremiumShopV2(
  state: GameState,
  serverNow: string,
  t: Translator,
  selectedRelic: PremiumShopRelic,
  previewSkinId: DiamondSkinId | null = null,
): string {
  return `<button class="modal-backdrop premium-shop-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
    t("sheet.close"),
  )}"></button><section class="premium-shop-dialog" role="dialog" aria-modal="true" aria-labelledby="premium-shop-title">
    <div class="premium-shop-runtime" id="premium-shop-runtime" style="background-image:url('${escapeHtml(
      PREMIUM_SHOP_ART.background.url,
    )}')" aria-hidden="true"></div>
    <div class="premium-shop-vignette" aria-hidden="true"></div>
    <header class="premium-shop-header"><div><span>${escapeHtml(t("shop.kicker"))}</span><h2 id="premium-shop-title">${escapeHtml(
      t("shop.title"),
    )}</h2><p>${escapeHtml(t("shop.lead"))}</p></div><button class="premium-shop-close" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("shop.backToGame"),
    )}"><span aria-hidden="true">←</span><strong>${escapeHtml(t("shop.backToGame"))}</strong></button></header>
    <div class="premium-shop-hero" aria-hidden="true"></div>
    <div class="premium-shop-content">${renderPremiumShopContentV2(state, serverNow, t, selectedRelic, previewSkinId)}</div>
  </section>`;
}

export function renderRewardRevealV2(t: Translator, achievementId: AchievementId, locale: GameState["settings"]["locale"] = "ru"): string {
  const keys = achievementKeys[achievementId];
  return `<button class="modal-backdrop reward-reveal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
    t("sheet.close"),
  )}"></button><section class="reward-reveal-dialog" role="dialog" aria-modal="true" aria-labelledby="reward-reveal-title">
    <div class="reward-reveal-runtime" id="reward-reveal-runtime" aria-hidden="true"></div>
    <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("sheet.close"),
    )}">${closeIcon()}</button>
    <header><span>${escapeHtml(t("rewardReveal.kicker"))}</span><h2 id="reward-reveal-title">${escapeHtml(
      t(keys.name),
    )}</h2><p>${escapeHtml(t(keys.body, { threshold: formatCurrencyAmount(GAME_CONFIG.referrals.activationPrimaryThreshold, locale) }))}</p></header>
    <div class="reward-reveal-rarity"><i aria-hidden="true"></i><span>${escapeHtml(t("rewardReveal.saved"))}</span><i aria-hidden="true"></i></div>
    <button class="reward-reveal-action" type="button" data-action="close-sheet">${escapeHtml(t("rewardReveal.continue"))}</button>
  </section>`;
}

function isDiamondSkinId(id: CollectibleId): id is DiamondSkinId {
  return Object.prototype.hasOwnProperty.call(PREMIUM_SHOP_ART.skins, id);
}

function collectibleArt(id: CollectibleId): TrophyArt {
  if (isDiamondSkinId(id)) return PREMIUM_SHOP_ART.skins[id];
  if (id === "season-01-signet") return PREMIUM_SHOP_ART.seasonSignet;
  if (id === "season-zero-participant") return PREMIUM_SHOP_ART.seasonZeroParticipant;
  return TROPHY_HALL_ART.achievements[id];
}

function collectibleName(id: CollectibleId, t: Translator): string {
  if (isDiamondSkinId(id)) return t(`shop.offer.${id}.name` as TranslationKey);
  if (id === "season-01-signet") return t("shop.offer.season-01-signet.name");
  if (id === "season-zero-participant") return t("collection.seasonZeroParticipant.name");
  return t(achievementKeys[id].name);
}

export function renderCollectibleV2(state: GameState, t: Translator, collectibleId: CollectibleId): string {
  const item = state.collection.items.find((candidate) => candidate.id === collectibleId);
  if (item === undefined) return "";
  const isAchievement = item.kind === "achievement";
  const selected = isAchievement && state.profile.selectedTitle === collectibleId;
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
    t("sheet.close"),
  )}"></button><section class="bottom-sheet collectible-sheet" role="dialog" aria-modal="true" aria-labelledby="collectible-title">
    <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("sheet.close"),
    )}">${closeIcon()}</button>
    ${trophyArt(collectibleArt(collectibleId), "collectible-sheet__art")}
    <header><span>${escapeHtml(t(`collection.rarity.${item.rarity}` as TranslationKey))}</span><h2 id="collectible-title">${escapeHtml(
      collectibleName(collectibleId, t),
    )}</h2><p>${escapeHtml(t(`collection.kind.${item.kind}` as TranslationKey))}</p></header>
    <dl><div><dt>${escapeHtml(t("collection.source"))}</dt><dd>${escapeHtml(t(`collection.source.${item.source}` as TranslationKey))}</dd></div><div><dt>${escapeHtml(
      t("collection.received"),
    )}</dt><dd>${escapeHtml(formatUtcDate(item.acquiredAt, state.settings.locale))}</dd></div><div><dt>NFT</dt><dd>${escapeHtml(
      t("collection.offChain"),
    )}</dd></div></dl>
    ${isAchievement ? `<button type="button" class="collectible-sheet__action" data-action="select-profile-title" data-achievement="${collectibleId}" ${selected ? "disabled" : ""}>${escapeHtml(
      selected ? t("profile.selectedTrophy") : t("profile.chooseTrophy"),
    )}</button>` : ""}
  </section>`;
}

export { renderKingdomProfile as renderProfileV2 } from "./profileTemplate";

export function renderRewardAccountV2(state: GameState, t: Translator): string {
  const locale = state.settings.locale;
  const ledger = [...state.rewardAccount.ledger].reverse();
  const entryTitle = (kind: GameState["rewardAccount"]["ledger"][number]["kind"]): TranslationKey => {
    if (kind === "partner-transfer-in") return "rewardAccount.entryPartnerIn";
    if (kind === "partner-transfer-out") return "rewardAccount.entryPartnerOut";
    if (kind === "liquidity-reserve") return "rewardAccount.entryLpReserve";
    if (kind === "liquidity-refund") return "rewardAccount.entryLpRefund";
    if (kind === "liquidity-created") return "rewardAccount.entryLpCreated";
    if (kind === "season-claim") return "rewardAccount.entrySeason";
    if (kind === "wallet-token-claim") return "rewardAccount.entryWallet";
    return "rewardAccount.entryDiamond";
  };
  const entries = ledger.length === 0
    ? `<li class="reward-account-empty"><strong>${escapeHtml(t("rewardAccount.emptyTitle"))}</strong><p>${escapeHtml(
        t("rewardAccount.emptyBody"),
      )}</p></li>`
    : ledger.map((entry) => `<li><span aria-hidden="true"><i></i></span><div><strong>${escapeHtml(
        t(entryTitle(entry.kind), { season: entry.seasonId ?? t("rewardAccount.externalOperation") }),
      )}</strong><small>${escapeHtml(formatUtcDate(entry.occurredAt, locale))}</small></div><em class="${amount(entry.referenceUsdtMicrosDelta) < 0n ? "is-debit" : "is-credit"}">${escapeHtml(
        `${amount(entry.referenceUsdtMicrosDelta) < 0n ? "−" : "+"}${formatUsdtMicros(
          (amount(entry.referenceUsdtMicrosDelta) < 0n ? -amount(entry.referenceUsdtMicrosDelta) : amount(entry.referenceUsdtMicrosDelta)).toString(),
          locale,
        )}`,
      )}</em></li>`).join("");
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
    t("sheet.close"),
  )}"></button>
    <section class="reward-account-sheet" data-testid="reward-account-sheet" data-reward-account-state="${ledger.length === 0 ? "empty" : "funded"}" role="dialog" aria-modal="true" aria-labelledby="reward-account-title">
      <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(
        t("sheet.close"),
      )}">${closeIcon()}</button>
      <div class="reward-account-runtime" id="reward-account-runtime" aria-hidden="true"></div>
      <header class="reward-account-heading"><span>${escapeHtml(t("rewardAccount.kicker"))}</span><h2 id="reward-account-title">${escapeHtml(
        t("rewardAccount.title"),
      )}</h2><p>${escapeHtml(t("rewardAccount.body"))}</p></header>
      <section class="reward-account-balance" aria-label="${escapeHtml(t("rewardAccount.balanceLabel"))}">
        <span>${escapeHtml(t("rewardAccount.balanceLabel"))}</span><strong>${escapeHtml(
          formatUsdtMicros(state.rewardAccount.referenceUsdtMicros, locale),
        )}</strong><small>${escapeHtml(t("rewardAccount.balanceHint"))}</small>
      </section>
      <button class="partner-transfer-gateway" type="button" data-action="open-partner-transfer"><img src="${MAKERS_CLUB_ART.nearSignet.url}" alt=""/><span><strong>${locale === "ru" ? "Передать мастеру" : "Support a master"}</strong><small>${locale === "ru" ? "Поддержать лично приглашённого партнёра" : "Support a personally invited partner"}</small></span><b aria-hidden="true">›</b></button>
      <section class="reward-account-history" aria-labelledby="reward-account-history-title"><header><span>${escapeHtml(
        t("rewardAccount.historyKicker"),
      )}</span><h3 id="reward-account-history-title">${escapeHtml(t("rewardAccount.historyTitle"))}</h3></header><ul>${entries}</ul></section>
      <aside class="reward-account-note" aria-labelledby="reward-account-note-title">
        <strong id="reward-account-note-title">${escapeHtml(t("rewardAccount.referenceTitle"))}</strong>
        <p>${escapeHtml(t("rewardAccount.referenceNote"))}</p>
        <span>${escapeHtml(t("rewardAccount.referenceRate", {
          value: formatUsdtMicros(state.season.plannedLaunchPriceUsdtMicrosPerToken, locale, 6),
        }))}</span>
      </aside>
    </section>`;
}

export function renderBalanceV2(state: GameState, serverNow: string, t: Translator): string {
  const locale = state.settings.locale;
  const projectionVisible = state.season.projection.visible;
  const projectionRevealTime = state.season.projection.revealsAt === null
    ? t("common.soon")
    : formatServerDuration(state.season.projection.revealsAt, serverNow, locale);
  const estimate = formatUsdtCents(state.season.projection.estimatedRewardUsdCents, locale, true);
  const fund = formatUsdtCents(state.season.prizeFundUsdCents, locale);
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}"></button>
    <section class="bottom-sheet balance-sheet-v2" role="dialog" aria-modal="true" aria-labelledby="sheet-title">
      <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}">${closeIcon()}</button>
      <header class="balance-sheet-hero">
        <span class="balance-sheet-crystal" aria-hidden="true">${currencyIcon()}</span>
        <div><span class="sheet-kicker">${escapeHtml(t("balance.kicker"))}</span><h2 id="sheet-title">${escapeHtml(
          t("balance.hero"),
        )}</h2><p>${escapeHtml(t("balance.caratBody"))}</p><p>${escapeHtml(t("balance.caratSources"))}</p></div>
      </header>
      <section class="balance-vault" aria-label="${escapeHtml(t("balance.title"))}">
        <div class="balance-vault-total"><small>${escapeHtml(t("balance.availableNow"))}</small><div><span aria-hidden="true">${currencyIcon()}</span><strong data-live-value="available" data-digits="${state.account.available.replace("-", "").length}">${escapeHtml(
          formatAmount(state.account.available, locale),
        )}</strong></div></div>
        <div class="balance-vault-estimate${projectionVisible ? "" : " is-pending"}"><small>${escapeHtml(
          projectionVisible ? t("balance.estimateIfNow") : t("balance.estimatePreparing"),
        )}</small><div class="balance-reward-ornament"><i aria-hidden="true"></i><strong${projectionVisible ? " data-season-estimate-leading" : ' data-season-projection-countdown="balance"'}>${escapeHtml(
          projectionVisible ? estimate : t("balance.estimateRevealsIn", { time: projectionRevealTime }),
        )}</strong><i aria-hidden="true"></i></div><span>${escapeHtml(t("balance.estimateLabel"))}</span></div>
      </section>
      <div class="balance-season-fund"><span>${escapeHtml(t("balance.fund"))}</span><strong data-season-fund-leading>${escapeHtml(
        fund,
      )}</strong></div>
      ${
        state.account.reserved !== "0"
          ? `<div class="balance-reserved"><span>${escapeHtml(t("balance.reserved"))}</span><strong>${escapeHtml(
              formatCurrencyAmount(state.account.reserved, locale),
            )}</strong></div>`
          : ""
      }
      <section class="balance-reward-story" aria-labelledby="balance-reward-title"><h3 id="balance-reward-title">${escapeHtml(
        t("balance.rewardTitle"),
      )}</h3><ol><li><span>1</span><p>${escapeHtml(t("balance.rewardStepFund"))}</p></li><li><span>2</span><p>${escapeHtml(
        t("balance.rewardStepBalance"),
      )}</p></li><li><span>3</span><p>${escapeHtml(t("balance.rewardStepShare"))}</p></li></ol></section>
      <p class="balance-estimate-note">${escapeHtml(
        projectionVisible ? t("balance.estimateChanges") : t("balance.estimateWarmupBody"),
      )}</p>
      ${projectionVisible ? "" : `<p class="balance-estimate-note balance-estimate-note--warmup">${escapeHtml(t("balance.estimateWarmupNote"))}</p>`}
    </section>`;
}

export function renderUpgradeV2(state: GameState, t: Translator, code: ProductionUpgradeCode): string {
  const context: TemplateContext = { state, serverNow: state.meta.lastProductionAt, t };
  const currentLevel = getUpgradeLevel(state, code);
  const current = getUpgradeLevelConfig(code, currentLevel);
  const next = getNextUpgradeLevel(state, code);
  const canAfford = next?.costToReach != null && amount(state.account.available) >= amount(next.costToReach);
  const canBuy = canAfford && state.season.status === "active";
  const body = code === "precisionCutter" ? t("workshop.precision.sheet") : t("workshop.mechanical.sheet");
  const currentDisplay = effectDisplay(code, current.effectValue, t, state.settings.locale);
  const nextStage = stageAfterNextUpgrade(state, code);
  const appearanceChanges = next !== null && nextStage > state.workshopStage;
  const actionLabel = upgradeActionLabel(code, currentLevel, t);
  const missing = next?.costToReach == null ? 0n : amount(next.costToReach) - amount(state.account.available);
  const delta =
    next === null
      ? null
      : effectDisplay(code, (amount(next.effectValue) - amount(current.effectValue)).toString(), t, state.settings.locale);
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}"></button>
    <section class="bottom-sheet upgrade-sheet-v2" role="dialog" aria-modal="true" aria-labelledby="sheet-title">
      <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}">${closeIcon()}</button>
      <div class="upgrade-sheet-hero">
        <div class="upgrade-sheet-machine upgrade-sheet-machine--${code === "precisionCutter" ? "precision" : "mechanical"}" aria-hidden="true"><i></i></div>
        <div class="upgrade-sheet-copy"><span class="sheet-kicker">${escapeHtml(
          t(code === "precisionCutter" ? "development.faceting" : "development.mechanisms"),
        )}</span><h2 id="sheet-title">${escapeHtml(upgradeName(code, t))}</h2><p>${escapeHtml(body)}</p></div>
      </div>
      <div class="upgrade-sheet-rate"><span>${escapeHtml(
        t(code === "precisionCutter" ? "workshop.tapStrength" : "workshop.productionSpeed"),
      )}</span>${
        next === null
          ? `<div class="upgrade-sheet-current"><strong>${escapeHtml(currentDisplay.value)}</strong><small>${escapeHtml(
              currentDisplay.unit,
            )}</small></div>`
          : effectComparison(code, current.effectValue, next.effectValue, t, state.settings.locale, "upgrade-sheet-readout")
      }</div>
      ${
        next?.costToReach == null || delta === null
          ? ""
          : `<dl class="upgrade-sheet-ledger"><div><dt>${escapeHtml(
              t(code === "precisionCutter" ? "workshop.sheetGainTap" : "workshop.sheetGainPassive"),
            )}</dt><dd>+${escapeHtml(delta.value)} <small>${escapeHtml(delta.unit)}</small></dd></div><div><dt>${escapeHtml(
              t("workshop.sheetCost"),
            )}</dt><dd>${currency(next.costToReach, context)}</dd></div></dl>`
      }
      ${
        appearanceChanges
          ? `<p class="upgrade-visual-note"><span aria-hidden="true">✦</span>${escapeHtml(t("workshop.appearanceWillChange"))}</p>`
          : ""
      }
      ${
        next?.costToReach == null
          ? `<div class="forge-complete"><span aria-hidden="true">✦</span><strong>${escapeHtml(t("workshop.max"))}</strong></div>`
          : `<button class="forge-upgrade upgrade-sheet-action" type="button" data-action="buy-upgrade" data-upgrade="${code}" ${
              canBuy ? "" : "disabled"
            } aria-label="${escapeHtml(`${actionLabel}, ${formatCurrencyAmount(next.costToReach, state.settings.locale)}`)}"><span>${escapeHtml(
              actionLabel,
            )}</span>${currency(next.costToReach, context)}</button>${
              missing > 0n
                ? `<p class="forge-shortfall" data-live-shortfall="${code}" aria-live="polite"><span aria-hidden="true">◇</span><strong data-live-shortfall-value>${escapeHtml(
                    t("workshop.shortfall", { value: formatCurrencyAmount(missing.toString(), state.settings.locale) }),
                  )}</strong></p>`
                : ""
            }`
      }
    </section>`;
}

export function renderUpgradeConfirmationV2(state: GameState, t: Translator, code: UpgradeCode, feedback: SpendFeedback | null = null): string {
  const next = getNextUpgradeLevel(state, code);
  if (next?.costToReach == null) return "";
  const context: TemplateContext = { state, serverNow: state.meta.lastProductionAt, t };
  const name = code === "precisionCutter" || code === "mechanicalCutter"
    ? upgradeName(code, t)
    : t(code === "energyCapacity" ? "energy.capacityName" : "energy.recoveryName");
  const maximumLevel = GAME_CONFIG.upgrades[code].levels.at(-1)?.level ?? next.level;
  const balanceAfter = amount(state.account.available) - amount(next.costToReach);
  const canAfford = balanceAfter >= 0n && state.season.status === "active";
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(
    t("sheet.close"),
  )}"></button><section class="bottom-sheet upgrade-confirm-sheet" role="dialog" aria-modal="true" aria-labelledby="upgrade-confirm-title" aria-describedby="upgrade-confirm-description">
    <button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(
      t("sheet.close"),
    )}">${closeIcon()}</button>
    <div class="upgrade-confirm-sheet__seal" aria-hidden="true"><span>✦</span></div>
    <header><span class="sheet-kicker">${escapeHtml(t("upgradeConfirm.kicker"))}</span><h2 id="upgrade-confirm-title">${escapeHtml(
      t("upgradeConfirm.title", { name }),
    )}</h2><p id="upgrade-confirm-description">${escapeHtml(
      t("upgradeConfirm.body", { level: next.level, max: maximumLevel }),
    )}</p></header>
    <dl class="upgrade-confirm-sheet__ledger"><div><dt>${escapeHtml(t("upgradeConfirm.balance"))}</dt><dd>${currency(
      state.account.available,
      context,
    )}</dd></div><div><dt>${escapeHtml(t("upgradeConfirm.cost"))}</dt><dd class="is-cost">−${currency(
      next.costToReach,
      context,
    )}</dd></div><div><dt>${escapeHtml(t("upgradeConfirm.after"))}</dt><dd>${currency(
      balanceAfter.toString(),
      context,
    )}</dd></div></dl>
    <p class="upgrade-confirm-sheet__note"><span aria-hidden="true">◇</span>${escapeHtml(t("upgradeConfirm.note"))}</p>
    <div class="upgrade-confirm-sheet__actions"><button type="button" data-action="close-sheet">${escapeHtml(
      t("upgradeConfirm.cancel"),
    )}</button><button type="button" data-action="confirm-upgrade" data-upgrade="${code}" ${feedback !== null ? 'aria-busy="true"' : ""} ${canAfford && feedback === null ? "" : "disabled"}>${escapeHtml(
      t(feedback === null ? "upgradeConfirm.confirm" : feedback.phase === "saving" ? "spend.saving" : "spend.sending"),
    )}</button></div>
    ${renderSpendFeedback(feedback, t)}
  </section>`;
}
